export interface Flashcard {
  id: number;
  question: string;
  answer: string;
  category: string;
  difficulty: "easy" | "medium" | "hard";
  explanation?: string;
}

export const flashcards: Flashcard[] = [
  {
    id: 1,
    question: "What is the primary purpose of a real estate contract?",
    answer: "To create legally binding obligations between parties in a real estate transaction.",
    category: "Real Estate Contracts & Agency",
    difficulty: "easy",
    explanation: "Contracts establish the terms, conditions, and responsibilities for all parties involved in buying, selling, or leasing real property."
  },
  {
    id: 2,
    question: "What are the essential elements required for a valid real estate contract?",
    answer: "Offer, acceptance, consideration, competent parties, legal purpose, and written form (for real estate).",
    category: "Real Estate Contracts & Agency",
    difficulty: "medium",
    explanation: "All six elements must be present for a contract to be legally enforceable. Real estate contracts must be in writing due to the Statute of Frauds."
  },
  {
    id: 3,
    question: "What is the difference between an express contract and an implied contract?",
    answer: "Express contracts have terms explicitly stated, while implied contracts are created by actions or circumstances.",
    category: "Real Estate Contracts & Agency",
    difficulty: "medium",
    explanation: "Express contracts are clearly written or spoken agreements. Implied contracts arise from conduct that suggests mutual agreement."
  },
  {
    id: 4,
    question: "What is unilateral contract versus bilateral contract?",
    answer: "Unilateral: one party makes a promise in exchange for performance. Bilateral: both parties exchange promises.",
    category: "Real Estate Contracts & Agency",
    difficulty: "medium",
    explanation: "Most real estate contracts are bilateral, with both buyer and seller making promises to each other."
  },
  {
    id: 5,
    question: "What is the Statute of Frauds and how does it apply to real estate?",
    answer: "A law requiring certain contracts to be in writing to be enforceable, including all real estate contracts.",
    category: "Real Estate Contracts & Agency",
    difficulty: "hard",
    explanation: "Prevents fraud by requiring written evidence of real estate agreements. Oral real estate contracts are generally unenforceable."
  },
  {
    id: 6,
    question: "What is an executory contract versus an executed contract?",
    answer: "Executory: obligations remain to be performed. Executed: all obligations have been completed.",
    category: "Real Estate Contracts & Agency",
    difficulty: "medium",
    explanation: "Most real estate purchase agreements are executory until closing, when they become executed."
  },
  {
    id: 7,
    question: "What is the difference between void and voidable contracts?",
    answer: "Void: never legally binding. Voidable: valid but can be canceled by one party due to legal defects.",
    category: "Real Estate Contracts & Agency",
    difficulty: "hard",
    explanation: "Void contracts lack essential elements. Voidable contracts have defects like fraud, duress, or incapacity."
  },
  {
    id: 8,
    question: "What is consideration in a real estate contract?",
    answer: "Something of value exchanged between parties, such as money, property, or services.",
    category: "Real Estate Contracts & Agency",
    difficulty: "easy",
    explanation: "Consideration makes the contract legally binding. In real estate, this is typically the purchase price and property."
  },
  {
    id: 9,
    question: "What is a counteroffer in real estate transactions?",
    answer: "A rejection of the original offer with a new offer containing different terms.",
    category: "Real Estate Contracts & Agency",
    difficulty: "medium",
    explanation: "A counteroffer terminates the original offer. The original offeror can accept, reject, or make another counteroffer."
  },
  {
    id: 10,
    question: "What is the difference between assignment and novation?",
    answer: "Assignment: transfer of rights to another party. Novation: substitution of a new party with release of original party.",
    category: "Real Estate Contracts & Agency",
    difficulty: "hard",
    explanation: "Assignment keeps original party liable. Novation releases original party and substitutes new party."
  },
  {
    id: 11,
    question: "What is an agency relationship in real estate?",
    answer: "A fiduciary relationship where an agent represents a principal (client) in real estate transactions.",
    category: "Real Estate Contracts & Agency",
    difficulty: "easy",
    explanation: "The agent owes fiduciary duties to the principal, including loyalty, confidentiality, and obedience."
  },
  {
    id: 12,
    question: "What are the different types of agency relationships?",
    answer: "Seller's agent, buyer's agent, dual agent, designated agent, and transaction broker.",
    category: "Real Estate Contracts & Agency",
    difficulty: "medium",
    explanation: "Each type defines whom the agent represents and the duties owed to different parties."
  },
  {
    id: 13,
    question: "A real estate agent has been working with a buyer for months. The buyer wants to make an offer on a property that the agent's brokerage has listed. What must happen before the agent can represent both parties?",
    answer: "The agent must obtain informed written consent from both the buyer and seller, disclosing the dual agency relationship and its implications.",
    category: "Real Estate Contracts & Agency",
    difficulty: "medium",
    explanation: "Dual agency requires full disclosure and written consent from both parties because the agent cannot fully represent either party's best interests when representing both sides. Without proper consent, the agent cannot proceed with dual representation."
  },
  {
    id: 14,
    question: "What is designated agency?",
    answer: "A broker appoints separate agents within the same firm to represent buyer and seller.",
    category: "Real Estate Contracts & Agency",
    difficulty: "medium",
    explanation: "Avoids dual agency conflicts by having different agents represent each party in the same brokerage."
  },
  {
    id: 15,
    question: "What is a transaction broker?",
    answer: "An agent who facilitates a transaction without representing either party as a fiduciary.",
    category: "Real Estate Contracts & Agency",
    difficulty: "medium",
    explanation: "Provides limited services without fiduciary duties. Must disclose this relationship to all parties."
  },
  {
    id: 16,
    question: "What are the fiduciary duties owed by an agent to a principal?",
    answer: "Loyalty, confidentiality, obedience, accounting, disclosure, and reasonable care.",
    category: "Real Estate Contracts & Agency",
    difficulty: "hard",
    explanation: "These duties form the foundation of the agency relationship and must be maintained throughout."
  },
  {
    id: 17,
    question: "What is the duty of loyalty in agency relationships?",
    answer: "The agent must act solely in the principal's best interest, not their own or others' interests.",
    category: "Real Estate Contracts & Agency",
    difficulty: "medium",
    explanation: "This includes avoiding conflicts of interest and disclosing any personal interests in transactions."
  },
  {
    id: 18,
    question: "What is the duty of confidentiality?",
    answer: "The agent must keep confidential information about the principal private.",
    category: "Real Estate Contracts & Agency",
    difficulty: "easy",
    explanation: "This duty continues even after the agency relationship ends, unless disclosure is required by law."
  },
  {
    id: 19,
    question: "A seller tells the agent their bottom-line price and asks the agent not to disclose it. Under the duty of confidentiality, when may the agent disclose this information?",
    answer: "Only with the client's permission, when disclosure is required by law, or to prevent fraud/material misrepresentation.",
    category: "Real Estate Contracts & Agency",
    difficulty: "hard",
    explanation: "This scenario tests the application of fiduciary duties in real-world situations. Agents must balance client confidentiality with legal obligations to disclose material facts."
  },
  {
    id: 20,
    question: "What is the duty of accounting?",
    answer: "The agent must account for all money and property received on behalf of the principal.",
    category: "Real Estate Contracts & Agency",
    difficulty: "medium",
    explanation: "This includes maintaining accurate records and promptly delivering funds to the principal."
  },
  {
    id: 21,
    question: "What is the duty of disclosure?",
    answer: "The agent must reveal all material facts that could affect the principal's decision.",
    category: "Real Estate Contracts & Agency",
    difficulty: "medium",
    explanation: "This includes property defects, market conditions, and any conflicts of interest."
  },
  {
    id: 22,
    question: "What is the duty of reasonable care?",
    answer: "The agent must use skill and knowledge appropriate for their profession.",
    category: "Real Estate Contracts & Agency",
    difficulty: "easy",
    explanation: "This includes staying informed about market conditions and property values."
  },
  {
    id: 23,
    question: "What is an agency disclosure statement?",
    answer: "A document explaining whom the agent represents and the nature of the agency relationship.",
    category: "Real Estate Contracts & Agency",
    difficulty: "medium",
    explanation: "Required in most states to inform consumers about agency relationships before representation begins."
  },
  {
    id: 24,
    question: "What is a listing agreement?",
    answer: "A contract between a seller and broker authorizing the broker to sell the property.",
    category: "Real Estate Contracts & Agency",
    difficulty: "easy",
    explanation: "Establishes the agency relationship and terms for marketing and selling the property."
  },
  {
    id: 25,
    question: "What are the different types of listing agreements?",
    answer: "Exclusive right to sell, exclusive agency, open listing, and net listing.",
    category: "Real Estate Contracts & Agency",
    difficulty: "medium",
    explanation: "Each type defines the broker's compensation rights and exclusivity period."
  },
  {
    id: 26,
    question: "What is an exclusive right to sell listing?",
    answer: "The broker earns commission regardless of who finds the buyer, even if the seller finds the buyer.",
    category: "Real Estate Contracts & Agency",
    difficulty: "medium",
    explanation: "This provides the broker with the strongest protection and highest commission security."
  },
  {
    id: 27,
    question: "What is an exclusive agency listing?",
    answer: "The broker earns commission unless the seller finds the buyer without broker assistance.",
    category: "Real Estate Contracts & Agency",
    difficulty: "medium",
    explanation: "The seller can avoid paying commission by finding their own buyer."
  },
  {
    id: 28,
    question: "What is an open listing?",
    answer: "Multiple brokers can market the property, and only the broker who finds the buyer earns commission.",
    category: "Real Estate Contracts & Agency",
    difficulty: "medium",
    explanation: "Creates competition among brokers but may result in less marketing effort from each."
  },
  {
    id: 29,
    question: "What is a net listing?",
    answer: "The broker keeps any amount above the seller's net price as commission.",
    category: "Real Estate Contracts & Agency",
    difficulty: "hard",
    explanation: "Illegal in many states due to potential conflicts of interest and fraud."
  },
  {
    id: 30,
    question: "What is a buyer representation agreement?",
    answer: "A contract establishing an agency relationship between a buyer and broker.",
    category: "Real Estate Contracts & Agency",
    difficulty: "easy",
    explanation: "Defines the broker's duties to the buyer and compensation terms."
  },
  {
    id: 31,
    question: "What is procuring cause?",
    answer: "The broker who initiates the chain of events leading to a sale is entitled to commission.",
    category: "Real Estate Contracts & Agency",
    difficulty: "hard",
    explanation: "Determines which broker earns commission when multiple brokers are involved in a transaction."
  },
  {
    id: 32,
    question: "What is a net listing and why is it illegal in Texas?",
    answer: "A listing where the broker's commission is the difference between the sale price and a net amount the seller wants to receive. It's illegal because it creates a conflict of interest.",
    category: "Real Estate Contracts & Agency",
    difficulty: "hard",
    explanation: "Net listings are illegal in Texas because they incentivize the broker to get the highest possible price (to maximize their commission) while the seller may want to sell quickly at a lower price. This conflict violates fiduciary duty."
  },
  {
    id: 33,
    question: "What is the difference between an exclusive agency listing and an exclusive right to sell listing?",
    answer: "Exclusive agency: seller can sell without paying commission. Exclusive right to sell: broker earns commission regardless of who finds the buyer.",
    category: "Real Estate Contracts & Agency",
    difficulty: "medium",
    explanation: "The key difference is whether the seller can avoid paying commission by finding their own buyer. In exclusive right to sell, the broker is protected even if the seller finds the buyer."
  },
  {
    id: 34,
    question: "What is a listing period?",
    answer: "The time period during which the listing agreement is in effect, typically 90 days to 6 months, during which the broker has the right to market and sell the property.",
    category: "Real Estate Contracts & Agency",
    difficulty: "easy",
    explanation: "The listing period is specified in the listing agreement. If the property sells after the listing expires, the broker may still be entitled to commission if they were the procuring cause."
  },
  {
    id: 35,
    question: "What is a protection period in a listing agreement?",
    answer: "A period after the listing expires during which the broker can still earn commission if the property sells to someone the broker showed during the listing period.",
    category: "Real Estate Contracts & Agency",
    difficulty: "medium",
    explanation: "Protection periods (typically 30-90 days) protect the broker's right to commission for buyers they introduced, even if the sale occurs after the listing expires."
  },
  {
    id: 36,
    question: "What is a multiple listing service (MLS) and how does it benefit sellers?",
    answer: "A database where brokers share property listings. It benefits sellers by exposing their property to all MLS members and their buyers, increasing market exposure.",
    category: "Real Estate Practice",
    difficulty: "easy",
    explanation: "MLS dramatically increases a property's exposure by making it available to all member brokers and their buyers, typically resulting in faster sales and better prices."
  },
  {
    id: 37,
    question: "What information must be included in a listing agreement?",
    answer: "Property description, listing price, commission rate, listing period, broker's duties, and terms of representation.",
    category: "Real Estate Contracts & Agency",
    difficulty: "medium",
    explanation: "A complete listing agreement specifies all essential terms including the property, price, compensation, duration, and the scope of the broker's representation and marketing efforts."
  },
  {
    id: 38,
    question: "What factors indicate a buyer's market?",
    answer: "High inventory, longer days on market, price reductions, and buyers having more negotiating power with contingencies and repairs.",
    category: "Property Value & Appraisal",
    difficulty: "medium",
    explanation: "In a buyer's market, supply exceeds demand. Properties stay on the market longer, sellers may need to reduce prices, and buyers can negotiate more favorable terms including price, contingencies, and repairs."
  },
  {
    id: 39,
    question: "What factors indicate a seller's market?",
    answer: "Low inventory, quick sales, multiple offers, properties selling at or above asking price, and buyers waiving contingencies.",
    category: "Property Value & Appraisal",
    difficulty: "medium",
    explanation: "In a seller's market, demand exceeds supply. Properties sell quickly, often with multiple offers, and buyers may need to offer above asking price or waive contingencies to compete."
  },
  {
    id: 40,
    question: "What are the key assumptions in the definition of market value?",
    answer: "Willing buyer and seller, both parties are informed, reasonable exposure time, normal market conditions, and payment in cash or equivalent terms.",
    category: "Property Value & Appraisal",
    difficulty: "hard",
    explanation: "Market value assumes an arm's-length transaction between knowledgeable parties, adequate marketing time, and typical financing terms. These assumptions distinguish market value from other value types like investment value or assessed value."
  },
  {
    id: 41,
    question: "How does assessed value differ from market value in Texas?",
    answer: "Assessed value is set by county appraisal districts for tax purposes and may be lower than market value. Market value is what a property would sell for in the open market.",
    category: "Property Value & Appraisal",
    difficulty: "medium",
    explanation: "Texas counties assess property at market value, but the assessed value used for taxes may be lower due to exemptions, caps, or assessment ratios. Property owners can protest if they believe the assessment is too high."
  },
  {
    id: 42,
    question: "What is reproduction cost versus replacement cost?",
    answer: "Reproduction cost is rebuilding with exact same materials and design. Replacement cost is rebuilding with similar utility using modern materials and methods.",
    category: "Property Value & Appraisal",
    difficulty: "hard",
    explanation: "Reproduction cost recreates the exact original, while replacement cost creates a functionally equivalent property. Replacement cost is more commonly used in appraisals because it reflects current construction methods and materials."
  },
  {
    id: 43,
    question: "What is the difference between curable and incurable functional obsolescence?",
    answer: "Curable obsolescence can be fixed economically (like outdated fixtures). Incurable obsolescence cannot be fixed economically (like poor floor plan that would require major reconstruction).",
    category: "Property Value & Appraisal",
    difficulty: "hard",
    explanation: "The key is whether the cost to fix the obsolescence is less than the value it would add. If fixing costs more than the value gained, it's incurable. Appraisers adjust value accordingly."
  },
  {
    id: 44,
    question: "What is physical deterioration versus economic obsolescence?",
    answer: "Physical deterioration is wear and tear on the property itself. Economic obsolescence is external factors like neighborhood decline or environmental issues that reduce value.",
    category: "Property Value & Appraisal",
    difficulty: "hard",
    explanation: "Physical deterioration can often be cured through maintenance and repairs. Economic obsolescence is typically incurable because it's caused by external factors beyond the property owner's control, such as nearby pollution, traffic, or neighborhood changes."
  },
  {
    id: 45,
    question: "What is a purchase agreement?",
    answer: "A contract outlining the terms and conditions for buying and selling real property.",
    category: "Real Estate Contracts & Agency",
    difficulty: "easy",
    explanation: "Also called a sales contract or purchase and sale agreement."
  },
  {
    id: 46,
    question: "What are the essential terms in a purchase agreement?",
    answer: "Parties, property description, purchase price, financing terms, closing date, and contingencies.",
    category: "Real Estate Contracts & Agency",
    difficulty: "medium",
    explanation: "These terms must be clearly defined to avoid disputes and ensure enforceability."
  },
  {
    id: 47,
    question: "What is earnest money?",
    answer: "A deposit made by the buyer to show good faith and commitment to the purchase.",
    category: "Real Estate Contracts & Agency",
    difficulty: "easy",
    explanation: "Held in escrow and applied to the purchase price at closing or forfeited if buyer defaults."
  },
  {
    id: 48,
    question: "A buyer client asks the agent to conceal a foundation issue discovered during inspection. What must the agent do to comply with Texas law and fiduciary duties?",
    answer: "Disclose known material facts; do not conceal defects; advise the client on obligations under the contract and law.",
    category: "Real Estate Contracts & Agency",
    difficulty: "hard",
    explanation: "Elevates a generic concept into a material-fact disclosure scenario. This practical application question tests understanding beyond simple definitions."
  },
  {
    id: 49,
    question: "What is a financing contingency?",
    answer: "A condition allowing the buyer to cancel if they cannot obtain financing.",
    category: "Real Estate Contracts & Agency",
    difficulty: "medium",
    explanation: "Protects the buyer from being forced to close without adequate financing."
  },
  {
    id: 50,
    question: "What is an inspection contingency?",
    answer: "A condition allowing the buyer to cancel or negotiate repairs based on inspection results.",
    category: "Real Estate Contracts & Agency",
    difficulty: "medium",
    explanation: "Gives the buyer time to discover property defects and negotiate remedies."
  },
  {
    id: 51,
    question: "What is an appraisal contingency?",
    answer: "A condition allowing the buyer to cancel if the property doesn't appraise for the purchase price.",
    category: "Real Estate Contracts & Agency",
    difficulty: "medium",
    explanation: "Protects the buyer from overpaying and ensures lender approval."
  },
  {
    id: 52,
    question: "What is a home sale contingency?",
    answer: "A condition allowing the buyer to cancel if they cannot sell their current home.",
    category: "Real Estate Contracts & Agency",
    difficulty: "medium",
    explanation: "Common when buyers need proceeds from their current home to purchase the new one."
  },
  {
    id: 53,
    question: "What is a lease agreement?",
    answer: "A contract between a landlord and tenant for the rental of real property.",
    category: "Real Estate Contracts & Agency",
    difficulty: "easy",
    explanation: "Establishes the terms of the rental relationship, including rent, duration, and responsibilities."
  },
  {
    id: 54,
    question: "What is a periodic tenancy?",
    answer: "A lease that automatically renews for successive periods until terminated.",
    category: "Real Estate Contracts & Agency",
    difficulty: "medium",
    explanation: "Examples include month-to-month or week-to-week tenancies."
  },
  {
    id: 55,
    question: "What is a tenancy at sufferance?",
    answer: "A tenant who remains in possession after the lease expires without permission.",
    category: "Real Estate Contracts & Agency",
    difficulty: "hard",
    explanation: "Also called a holdover tenant, subject to eviction proceedings."
  },
  {
    id: 56,
    question: "What is constructive eviction?",
    answer: "A landlord's actions that make the property uninhabitable, forcing the tenant to leave.",
    category: "Real Estate Contracts & Agency",
    difficulty: "hard",
    explanation: "The tenant can terminate the lease and may sue for damages."
  },
  {
    id: 57,
    question: "What is an option contract?",
    answer: "A contract giving one party the right to buy or sell property at a future date.",
    category: "Real Estate Contracts & Agency",
    difficulty: "medium",
    explanation: "The option holder pays for the right but is not obligated to exercise it."
  },
  {
    id: 58,
    question: "What is a right of first refusal?",
    answer: "A contract giving someone the first opportunity to buy property before it's offered to others.",
    category: "Real Estate Contracts & Agency",
    difficulty: "medium",
    explanation: "The holder can match any offer the seller receives from another party."
  },
  {
    id: 59,
    question: "What is a land contract?",
    answer: "A contract where the seller finances the purchase, with title transferring after final payment.",
    category: "Real Estate Contracts & Agency",
    difficulty: "hard",
    explanation: "Also called a contract for deed or installment sale contract."
  },
  {
    id: 60,
    question: "What is a lease-option agreement?",
    answer: "A lease that includes an option for the tenant to purchase the property.",
    category: "Real Estate Contracts & Agency",
    difficulty: "medium",
    explanation: "Combines rental payments with the right to buy at a predetermined price."
  },
  {
    id: 61,
    question: "What is a lease-purchase agreement?",
    answer: "A lease that obligates the tenant to purchase the property at the end of the lease.",
    category: "Real Estate Contracts & Agency",
    difficulty: "medium",
    explanation: "Different from lease-option because purchase is mandatory, not optional."
  },
  {
    id: 62,
    question: "In the TREC One to Four Family Residential Contract, which contingency allows the buyer to terminate if the lender's appraisal is below the sales price and the parties cannot renegotiate?",
    answer: "The Third Party Financing Addendum (appraisal/financing contingency).",
    category: "Real Estate Contracts & Agency",
    difficulty: "medium",
    explanation: "TREC-promulgated contract forms are standardized forms that must be used in Texas real estate transactions. Understanding specific addenda and contingencies is essential for exam success."
  },
  {
    id: 63,
    question: "What is mutual rescission?",
    answer: "Both parties agree to cancel a contract and return to their original positions.",
    category: "Real Estate Contracts & Agency",
    difficulty: "medium",
    explanation: "Allows parties to terminate a contract without breach or legal consequences."
  },
  {
    id: 64,
    question: "What is anticipatory breach?",
    answer: "One party indicates they will not perform their contractual obligations before the performance date.",
    category: "Real Estate Contracts & Agency",
    difficulty: "hard",
    explanation: "The non-breaching party can sue immediately without waiting for the performance date."
  },
  {
    id: 65,
    question: "What is specific performance?",
    answer: "A court order requiring a party to fulfill their contractual obligations.",
    category: "Real Estate Contracts & Agency",
    difficulty: "hard",
    explanation: "Common remedy in real estate because each property is considered unique."
  },
  {
    id: 66,
    question: "A buyer discovers that the seller never owned the property they contracted to sell. The buyer wants to cancel the contract and recover damages. What type of breach has occurred?",
    answer: "Material breach - a significant failure that goes to the heart of the contract, allowing the buyer to terminate and sue for damages.",
    category: "Real Estate Contracts & Agency",
    difficulty: "medium",
    explanation: "The seller's inability to transfer ownership is a fundamental breach that makes the contract's primary purpose impossible. This is a material breach, not a minor one, because it goes to the essence of the contract."
  },
  {
    id: 67,
    question: "What is minor breach?",
    answer: "A small failure to perform that doesn't substantially affect the contract.",
    category: "Real Estate Contracts & Agency",
    difficulty: "medium",
    explanation: "The contract remains in force, but the breaching party may owe damages."
  },
  {
    id: 68,
    question: "What is the doctrine of merger?",
    answer: "The deed merges with the contract, and contract terms are superseded by the deed.",
    category: "Real Estate Contracts & Agency",
    difficulty: "hard",
    explanation: "After closing, the deed becomes the primary document governing the transaction."
  },
  {
    id: 69,
    question: "What is the parol evidence rule?",
    answer: "Oral agreements made before or contemporaneous with a written contract cannot contradict the written terms.",
    category: "Real Estate Contracts & Agency",
    difficulty: "hard",
    explanation: "Protects the integrity of written contracts by preventing conflicting oral testimony."
  },
  {
    id: 70,
    question: "What is a contract of adhesion?",
    answer: "A contract with terms dictated by one party with little or no negotiation opportunity.",
    category: "Real Estate Contracts & Agency",
    difficulty: "hard",
    explanation: "Courts may interpret ambiguous terms against the drafting party."
  },
  {
    id: 71,
    question: "What is unconscionability?",
    answer: "A contract so unfair that it shocks the conscience and should not be enforced.",
    category: "Real Estate Contracts & Agency",
    difficulty: "hard",
    explanation: "Courts may refuse to enforce contracts with grossly unfair terms."
  },
  {
    id: 72,
    question: "What is undue influence in contract law?",
    answer: "Improper persuasion that overcomes someone's free will in entering a contract.",
    category: "Real Estate Contracts & Agency",
    difficulty: "medium",
    explanation: "Often involves relationships of trust and confidence, making the contract voidable."
  },
  {
    id: 73,
    question: "What is fraud in contract law?",
    answer: "Intentional misrepresentation of material facts to induce someone to enter a contract.",
    category: "Real Estate Contracts & Agency",
    difficulty: "medium",
    explanation: "Makes the contract voidable and may result in damages for the victim."
  },
  {
    id: 74,
    question: "What is misrepresentation?",
    answer: "A false statement of material fact that induces someone to enter a contract.",
    category: "Real Estate Contracts & Agency",
    difficulty: "medium",
    explanation: "Can be innocent, negligent, or fraudulent, affecting available remedies."
  },
  {
    id: 75,
    question: "What is mental incompetence in contract law?",
    answer: "Lack of mental capacity to understand the nature and consequences of a contract.",
    category: "Real Estate Contracts & Agency",
    difficulty: "medium",
    explanation: "Makes contracts voidable if proven at the time of contract formation."
  },
  {
    id: 76,
    question: "What is intoxication in contract law?",
    answer: "Being under the influence of drugs or alcohol when entering a contract.",
    category: "Real Estate Contracts & Agency",
    difficulty: "medium",
    explanation: "May make contracts voidable if it prevents understanding of the contract terms."
  },
  {
    id: 77,
    question: "What is illegality in contract law?",
    answer: "Contracts that violate law or public policy are void and unenforceable.",
    category: "Real Estate Contracts & Agency",
    difficulty: "medium",
    explanation: "Courts will not enforce contracts for illegal purposes or activities."
  },
  {
    id: 78,
    question: "What is impossibility of performance?",
    answer: "When circumstances make it impossible to fulfill contractual obligations.",
    category: "Real Estate Contracts & Agency",
    difficulty: "hard",
    explanation: "May excuse performance if the impossibility was not foreseeable and not the party's fault."
  },
  {
    id: 79,
    question: "What is impracticability of performance?",
    answer: "When performance becomes extremely difficult or expensive due to unforeseen circumstances.",
    category: "Real Estate Contracts & Agency",
    difficulty: "hard",
    explanation: "May excuse performance if the circumstances were unforeseeable and make performance commercially impracticable."
  },
  {
    id: 80,
    question: "What is frustration of purpose?",
    answer: "When the principal purpose of a contract is destroyed by unforeseen events.",
    category: "Real Estate Contracts & Agency",
    difficulty: "hard",
    explanation: "May excuse performance if the event was unforeseeable and destroys the contract's main purpose."
  },
  {
    id: 81,
    question: "What is force majeure?",
    answer: "Unforeseeable circumstances that prevent contract performance, such as natural disasters.",
    category: "Real Estate Contracts & Agency",
    difficulty: "medium",
    explanation: "Often included in contracts to excuse performance due to events beyond control."
  },
  {
    id: 82,
    question: "What is the mailbox rule?",
    answer: "An acceptance is effective when mailed, not when received.",
    category: "Real Estate Contracts & Agency",
    difficulty: "hard",
    explanation: "Applies to contract formation but not to contract modifications, which require receipt."
  },
  {
    id: 83,
    question: "What is the primary purpose of real estate licensing laws?",
    answer: "To protect the public by ensuring real estate professionals are qualified and ethical.",
    category: "Real Estate Practice",
    difficulty: "easy",
    explanation: "Licensing laws establish minimum standards for education, experience, and conduct."
  },
  {
    id: 84,
    question: "What is the difference between a real estate broker and a salesperson?",
    answer: "A broker can operate independently and supervise others, while a salesperson must work under a broker.",
    category: "Real Estate Practice",
    difficulty: "easy",
    explanation: "Brokers have higher education requirements and can own their own businesses."
  },
  {
    id: 85,
    question: "What is a real estate commission?",
    answer: "The state agency that regulates real estate licensees and enforces licensing laws.",
    category: "Real Estate Practice",
    difficulty: "easy",
    explanation: "Also called Real Estate Board or Department of Real Estate in some states."
  },
  {
    id: 86,
    question: "What is the purpose of continuing education requirements?",
    answer: "To ensure licensees stay current with laws, regulations, and industry practices.",
    category: "Real Estate Practice",
    difficulty: "easy",
    explanation: "Required for license renewal to maintain professional competence."
  },
  {
    id: 87,
    question: "What is the difference between a principal broker and an associate broker?",
    answer: "Principal broker owns or manages the brokerage, while associate broker works under supervision.",
    category: "Real Estate Practice",
    difficulty: "medium",
    explanation: "Associate brokers have broker licenses but choose to work under another broker."
  },
  {
    id: 88,
    question: "What is a trust fund account?",
    answer: "A special bank account for holding client funds separate from business funds.",
    category: "Real Estate Practice",
    difficulty: "medium",
    explanation: "Must be properly designated and maintained according to state regulations."
  },
  {
    id: 89,
    question: "What is commingling of funds?",
    answer: "Mixing client funds with personal or business funds, which is illegal.",
    category: "Real Estate Practice",
    difficulty: "medium",
    explanation: "A serious violation that can result in license suspension or revocation."
  },
  {
    id: 90,
    question: "What is the purpose of advertising regulations?",
    answer: "To ensure truthful, non-misleading advertising by real estate professionals.",
    category: "Real Estate Practice",
    difficulty: "easy",
    explanation: "Protects consumers from false or deceptive marketing practices."
  },
  {
    id: 91,
    question: "What information must be included in real estate advertising?",
    answer: "The broker's name and license status, and any team or agent names.",
    category: "Real Estate Practice",
    difficulty: "medium",
    explanation: "Requirements vary by state but generally include proper identification of licensees."
  },
  {
    id: 92,
    question: "What is the purpose of fair housing laws?",
    answer: "To prevent discrimination in housing based on protected characteristics.",
    category: "Real Estate Practice",
    difficulty: "easy",
    explanation: "Federal and state laws protect against housing discrimination."
  },
  {
    id: 93,
    question: "What are the protected classes under federal fair housing law?",
    answer: "Race, color, religion, sex, national origin, familial status, and disability.",
    category: "Real Estate Practice",
    difficulty: "medium",
    explanation: "These characteristics cannot be used to discriminate in housing decisions."
  },
  {
    id: 94,
    question: "What is steering in fair housing?",
    answer: "Directing buyers to or away from certain areas based on protected characteristics.",
    category: "Real Estate Practice",
    difficulty: "medium",
    explanation: "A form of discrimination that limits housing choices."
  },
  {
    id: 95,
    question: "What is blockbusting in fair housing?",
    answer: "Encouraging homeowners to sell by claiming minority groups are moving in.",
    category: "Real Estate Practice",
    difficulty: "medium",
    explanation: "An illegal practice that exploits racial fears to generate sales."
  },
  {
    id: 96,
    question: "What is redlining in fair housing?",
    answer: "Refusing to provide services or loans in certain areas based on demographics.",
    category: "Real Estate Practice",
    difficulty: "medium",
    explanation: "A discriminatory practice that limits access to housing and credit."
  },
  {
    id: 97,
    question: "What is the Americans with Disabilities Act (ADA)?",
    answer: "Federal law requiring accessibility accommodations for people with disabilities.",
    category: "Real Estate Practice",
    difficulty: "medium",
    explanation: "Applies to public accommodations and commercial properties."
  },
  {
    id: 98,
    question: "What is the Fair Housing Act?",
    answer: "Federal law prohibiting housing discrimination based on protected characteristics.",
    category: "Real Estate Practice",
    difficulty: "easy",
    explanation: "Also called Title VIII of the Civil Rights Act of 1968."
  },
  {
    id: 99,
    question: "What is the purpose of environmental disclosure laws?",
    answer: "To inform buyers about environmental hazards that may affect the property.",
    category: "Real Estate Practice",
    difficulty: "easy",
    explanation: "Protects buyers from undisclosed environmental risks."
  },
  {
    id: 100,
    question: "What is lead-based paint disclosure?",
    answer: "Required disclosure for properties built before 1978 about potential lead hazards.",
    category: "Real Estate Practice",
    difficulty: "medium",
    explanation: "Federal law requires disclosure and provides buyers with inspection rights."
  },
  {
    id: 101,
    question: "What is asbestos disclosure?",
    answer: "Required disclosure about asbestos-containing materials in properties.",
    category: "Real Estate Practice",
    difficulty: "medium",
    explanation: "Important for properties built before asbestos was banned in construction."
  },
  {
    id: 102,
    question: "What is the purpose of property condition disclosure?",
    answer: "To inform buyers about known defects and conditions affecting the property.",
    category: "Real Estate Practice",
    difficulty: "easy",
    explanation: "Helps buyers make informed decisions about property purchases."
  },
  {
    id: 103,
    question: "What is a property condition disclosure statement?",
    answer: "A form listing known defects and conditions that sellers must complete.",
    category: "Real Estate Practice",
    difficulty: "medium",
    explanation: "Required in many states to disclose material facts about the property."
  },
  {
    id: 104,
    question: "What is the purpose of material fact disclosure?",
    answer: "To inform parties about facts that could affect their decision-making.",
    category: "Real Estate Practice",
    difficulty: "easy",
    explanation: "Agents must disclose known material facts to all parties."
  },
  {
    id: 105,
    question: "What is a material fact?",
    answer: "A fact that could influence a reasonable person's decision about a property.",
    category: "Real Estate Practice",
    difficulty: "medium",
    explanation: "Includes defects, neighborhood issues, and other important information."
  },
  {
    id: 106,
    question: "What is the purpose of property inspection?",
    answer: "To discover defects and conditions that may affect the property's value or safety.",
    category: "Real Estate Practice",
    difficulty: "easy",
    explanation: "Helps buyers make informed decisions and negotiate repairs."
  },
  {
    id: 107,
    question: "What is a home inspection contingency?",
    answer: "A contract condition allowing buyers to inspect the property and negotiate repairs.",
    category: "Real Estate Practice",
    difficulty: "medium",
    explanation: "Gives buyers time to discover problems and decide how to proceed."
  },
  {
    id: 108,
    question: "What happens if a property doesn't appraise for the purchase price and there's no appraisal contingency?",
    answer: "The buyer may need to pay the difference in cash, renegotiate the price, or risk losing their earnest money if they cannot complete the purchase.",
    category: "Real Estate Practice",
    difficulty: "hard",
    explanation: "Without an appraisal contingency, the buyer is still obligated to purchase even if the appraisal is low. They must either come up with additional cash to cover the difference, negotiate a lower price with the seller, or potentially forfeit earnest money if they cannot perform."
  },
  {
    id: 109,
    question: "A married seller acquired title before marriage. The deed contains only the seller’s name. What additional step is commonly required to ensure marketable title at closing in Texas?",
    answer: "Obtain the spouse’s signature/joinder on the deed if the property is a homestead.",
    category: "Real Estate Practice",
    difficulty: "medium",
    explanation: "Applies recording/transfer concepts to a realistic closing scenario. This practical application question tests understanding beyond simple definitions."
  },
  {
    id: 110,
    question: "What is a title search?",
    answer: "An examination of public records to determine property ownership and liens.",
    category: "Real Estate Practice",
    difficulty: "medium",
    explanation: "Identifies any problems with the property's title before closing."
  },
  {
    id: 111,
    question: "What is the purpose of escrow?",
    answer: "To hold funds and documents safely until all contract conditions are met.",
    category: "Real Estate Practice",
    difficulty: "easy",
    explanation: "Provides neutral third-party handling of transaction elements."
  },
  {
    id: 112,
    question: "What is an escrow agent?",
    answer: "A neutral third party who holds funds and documents during a transaction.",
    category: "Real Estate Practice",
    difficulty: "easy",
    explanation: "Can be a title company, attorney, or other licensed professional."
  },
  {
    id: 113,
    question: "What is the purpose of closing?",
    answer: "To complete the real estate transaction by transferring ownership and funds.",
    category: "Real Estate Practice",
    difficulty: "easy",
    explanation: "Final step where all parties fulfill their contractual obligations."
  },
  {
    id: 114,
    question: "What is a closing statement?",
    answer: "A detailed accounting of all costs and credits for each party in a transaction.",
    category: "Real Estate Practice",
    difficulty: "medium",
    explanation: "Shows how funds are distributed and what each party owes or receives."
  },
  {
    id: 115,
    question: "What is the difference between constructive notice and actual notice?",
    answer: "Constructive notice is the legal presumption that everyone knows what's recorded in public records. Actual notice is direct, personal knowledge of facts.",
    category: "Real Estate Practice",
    difficulty: "hard",
    explanation: "Constructive notice applies to all recorded documents regardless of whether someone actually saw them. Actual notice requires personal knowledge. Both can affect property rights and priority of claims."
  },
  {
    id: 116,
    question: "What is inquiry notice?",
    answer: "Notice that a reasonable person would have discovered through investigation, even if they didn't actually know about it.",
    category: "Real Estate Practice",
    difficulty: "hard",
    explanation: "If circumstances would lead a reasonable person to investigate further (like a visible easement or obvious property use), they are considered to have inquiry notice, even without actual knowledge."
  },
  {
    id: 117,
    question: "What is the purpose of property taxes?",
    answer: "To fund local government services like schools, roads, and public safety.",
    category: "Real Estate Practice",
    difficulty: "easy",
    explanation: "Based on property value and assessed annually by local government."
  },
  {
    id: 118,
    question: "By what deadline must a homeowner file for a Texas homestead tax exemption to receive it for the current tax year?",
    answer: "Generally by April 30 of the current tax year (or as updated by local appraisal district rules).",
    category: "Real Estate Practice",
    difficulty: "easy",
    explanation: "Texas has specific deadlines for property tax exemptions and other important filings. Missing these deadlines can result in loss of benefits for the current tax year."
  },
  {
    id: 119,
    question: "What is property tax proration?",
    answer: "Dividing property taxes between buyer and seller based on ownership time.",
    category: "Real Estate Practice",
    difficulty: "medium",
    explanation: "Ensures each party pays taxes for the period they owned the property."
  },
  {
    id: 120,
    question: "What is the purpose of homeowners insurance?",
    answer: "To protect property owners from losses due to damage or liability claims.",
    category: "Real Estate Practice",
    difficulty: "easy",
    explanation: "Required by lenders and provides coverage for various risks."
  },
  {
    id: 121,
    question: "What is liability insurance?",
    answer: "Insurance coverage for injuries or damage caused to others on the property.",
    category: "Real Estate Practice",
    difficulty: "medium",
    explanation: "Protects property owners from lawsuits and claims."
  },
  {
    id: 122,
    question: "What is flood insurance?",
    answer: "Separate insurance coverage for flood damage, not included in standard policies.",
    category: "Real Estate Practice",
    difficulty: "medium",
    explanation: "Required in flood-prone areas and available through federal program."
  },
  {
    id: 123,
    question: "What is a property management agreement?",
    answer: "A contract between property owner and manager defining responsibilities.",
    category: "Real Estate Practice",
    difficulty: "medium",
    explanation: "Establishes the scope of services and compensation terms."
  },
  {
    id: 124,
    question: "What is the purpose of tenant screening?",
    answer: "To evaluate potential tenants' ability to pay rent and maintain the property.",
    category: "Real Estate Practice",
    difficulty: "easy",
    explanation: "Includes credit checks, employment verification, and references."
  },
  {
    id: 125,
    question: "What is the purpose of eviction proceedings?",
    answer: "To legally remove tenants who violate lease terms or fail to pay rent.",
    category: "Real Estate Practice",
    difficulty: "medium",
    explanation: "Must follow specific legal procedures and cannot involve self-help."
  },
  {
    id: 126,
    question: "What is depreciation in real estate investment?",
    answer: "A tax deduction for the theoretical wear and tear of investment property.",
    category: "Real Estate Practice",
    difficulty: "medium",
    explanation: "Non-cash expense that reduces taxable income."
  },
  {
    id: 127,
    question: "What is the purpose of real estate syndication?",
    answer: "To pool investor funds for larger real estate investments.",
    category: "Real Estate Practice",
    difficulty: "hard",
    explanation: "Allows smaller investors to participate in larger properties."
  },
  {
    id: 128,
    question: "What is the purpose of real estate investment trusts (REITs)?",
    answer: "To provide investors with real estate exposure without direct ownership.",
    category: "Real Estate Practice",
    difficulty: "medium",
    explanation: "Publicly traded companies that own and manage real estate portfolios."
  },
  {
    id: 129,
    question: "What is the purpose of zoning laws?",
    answer: "To regulate land use and development to protect public health and welfare.",
    category: "Real Estate Practice",
    difficulty: "easy",
    explanation: "Controls what types of buildings and activities are allowed in different areas."
  },
  {
    id: 130,
    question: "What is the purpose of real estate ethics?",
    answer: "To ensure professionals act with integrity and serve clients' best interests.",
    category: "Real Estate Practice",
    difficulty: "easy",
    explanation: "Establishes standards of conduct beyond legal requirements."
  },
  {
    id: 131,
    question: "What is market value?",
    answer: "The most probable price a property would sell for in a competitive market.",
    category: "Property Value & Appraisal",
    difficulty: "easy",
    explanation: "Based on typical market conditions, not distressed sales or special circumstances."
  },
  {
    id: 132,
    question: "What is the difference between market value and market price?",
    answer: "Market value is an estimate, while market price is what a property actually sells for.",
    category: "Property Value & Appraisal",
    difficulty: "medium",
    explanation: "Market price may differ from market value due to market conditions or negotiation."
  },
  {
    id: 133,
    question: "What is appraised value?",
    answer: "The value determined by a licensed appraiser for lending or other purposes.",
    category: "Property Value & Appraisal",
    difficulty: "easy",
    explanation: "Professional opinion of value based on appraisal standards and methods."
  },
  {
    id: 134,
    question: "What is replacement cost?",
    answer: "The cost to replace a property with one of similar quality and utility.",
    category: "Property Value & Appraisal",
    difficulty: "medium",
    explanation: "Used for insurance purposes and new construction valuation."
  },
  {
    id: 135,
    question: "What is the principle of highest and best use?",
    answer: "The most profitable legal use of a property that is physically possible and financially feasible.",
    category: "Property Value & Appraisal",
    difficulty: "hard",
    explanation: "Considers four criteria: legal permissibility, physical possibility, financial feasibility, and maximum productivity."
  },
  {
    id: 136,
    question: "What is the principle of substitution?",
    answer: "A buyer will not pay more for a property than the cost of acquiring a similar substitute.",
    category: "Property Value & Appraisal",
    difficulty: "medium",
    explanation: "Forms the basis for using comparable sales in appraisal."
  },
  {
    id: 137,
    question: "What is the principle of supply and demand?",
    answer: "Property values increase when demand exceeds supply and decrease when supply exceeds demand.",
    category: "Property Value & Appraisal",
    difficulty: "easy",
    explanation: "Basic economic principle affecting all real estate markets."
  },
  {
    id: 138,
    question: "What is the principle of progression?",
    answer: "A lower-value property benefits from being near higher-value properties.",
    category: "Property Value & Appraisal",
    difficulty: "medium",
    explanation: "The opposite of regression, where lower-value properties pull down higher-value ones."
  },
  {
    id: 139,
    question: "What is the principle of regression?",
    answer: "A higher-value property suffers from being near lower-value properties.",
    category: "Property Value & Appraisal",
    difficulty: "medium",
    explanation: "The opposite of progression, where lower-value properties negatively affect higher-value ones."
  },
  {
    id: 140,
    question: "What is the principle of contribution?",
    answer: "The value of a component is measured by its contribution to the whole property.",
    category: "Property Value & Appraisal",
    difficulty: "medium",
    explanation: "Used to determine if improvements add value equal to their cost."
  },
  {
    id: 141,
    question: "What is the principle of increasing and decreasing returns?",
    answer: "Adding improvements increases value up to a point, then returns diminish.",
    category: "Property Value & Appraisal",
    difficulty: "hard",
    explanation: "There's an optimal level of improvement beyond which additional investment doesn't increase value proportionally."
  },
  {
    id: 142,
    question: "What is the principle of balance?",
    answer: "Property value is maximized when land, labor, and capital are in proper balance.",
    category: "Property Value & Appraisal",
    difficulty: "hard",
    explanation: "An imbalance of these factors can reduce property value."
  },
  {
    id: 143,
    question: "What is the sales comparison approach?",
    answer: "An appraisal method that compares the subject property to similar recently sold properties.",
    category: "Property Value & Appraisal",
    difficulty: "medium",
    explanation: "Most commonly used approach for residential properties."
  },
  {
    id: 144,
    question: "What is the cost approach?",
    answer: "An appraisal method that estimates value based on land value plus replacement cost minus depreciation.",
    category: "Property Value & Appraisal",
    difficulty: "medium",
    explanation: "Often used for new construction or unique properties with few comparables."
  },
  {
    id: 145,
    question: "What is the income approach?",
    answer: "An appraisal method that estimates value based on the property's income-producing potential.",
    category: "Property Value & Appraisal",
    difficulty: "medium",
    explanation: "Primarily used for commercial and investment properties."
  },
  {
    id: 146,
    question: "What is net operating income (NOI)?",
    answer: "Gross rental income minus operating expenses (excluding debt service).",
    category: "Property Value & Appraisal",
    difficulty: "medium",
    explanation: "Used in income approach to determine property value."
  },
  {
    id: 147,
    question: "What is potential gross income?",
    answer: "The maximum income a property could generate if fully occupied at market rents.",
    category: "Property Value & Appraisal",
    difficulty: "medium",
    explanation: "Theoretical maximum income before considering vacancies or collection losses."
  },
  {
    id: 148,
    question: "What is functional obsolescence?",
    answer: "Depreciation due to design or layout problems that reduce utility or desirability.",
    category: "Property Value & Appraisal",
    difficulty: "hard",
    explanation: "Can be curable (fixable) or incurable (not economically feasible to fix)."
  },
  {
    id: 149,
    question: "What is external obsolescence?",
    answer: "Depreciation due to factors outside the property, such as neighborhood decline.",
    category: "Property Value & Appraisal",
    difficulty: "hard",
    explanation: "Usually incurable because the property owner cannot control external factors."
  },
  {
    id: 150,
    question: "What is a comparable sale?",
    answer: "A recently sold property similar to the subject property used in appraisal.",
    category: "Property Value & Appraisal",
    difficulty: "easy",
    explanation: "Should be similar in size, condition, location, and features."
  },
  {
    id: 151,
    question: "What is market analysis?",
    answer: "Study of supply, demand, and pricing trends in a specific market area.",
    category: "Property Value & Appraisal",
    difficulty: "medium",
    explanation: "Helps determine current market conditions and future trends."
  },
  {
    id: 152,
    question: "What is market segmentation?",
    answer: "Dividing the market into distinct groups with similar characteristics.",
    category: "Property Value & Appraisal",
    difficulty: "medium",
    explanation: "Helps identify target markets and pricing strategies."
  },
  {
    id: 153,
    question: "What is absorption rate?",
    answer: "The rate at which available properties are sold or leased in a market.",
    category: "Property Value & Appraisal",
    difficulty: "medium",
    explanation: "Indicates market health and helps predict future trends."
  },
  {
    id: 154,
    question: "What is a market study?",
    answer: "Comprehensive analysis of market conditions, trends, and factors affecting value.",
    category: "Property Value & Appraisal",
    difficulty: "medium",
    explanation: "Provides context for appraisal conclusions and market forecasts."
  },
  {
    id: 155,
    question: "What is market efficiency?",
    answer: "How quickly and accurately market prices reflect available information.",
    category: "Property Value & Appraisal",
    difficulty: "hard",
    explanation: "More efficient markets have prices that quickly adjust to new information."
  },
  {
    id: 156,
    question: "What is market cycle?",
    answer: "The recurring pattern of market expansion, peak, contraction, and recovery.",
    category: "Property Value & Appraisal",
    difficulty: "medium",
    explanation: "Understanding cycles helps predict market trends and timing."
  },
  {
    id: 157,
    question: "What is fee simple absolute?",
    answer: "The highest form of ownership with unlimited duration and no conditions.",
    category: "Forms of Ownership, Transfer & Recording",
    difficulty: "easy",
    explanation: "Also called fee simple, it's the most complete ownership interest possible."
  },
  {
    id: 158,
    question: "What is fee simple determinable?",
    answer: "Ownership that automatically ends when a condition is violated.",
    category: "Forms of Ownership, Transfer & Recording",
    difficulty: "hard",
    explanation: "The estate automatically reverts to the grantor without legal action."
  },
  {
    id: 159,
    question: "What is fee simple subject to condition subsequent?",
    answer: "Ownership that can be terminated by the grantor if conditions are violated.",
    category: "Forms of Ownership, Transfer & Recording",
    difficulty: "hard",
    explanation: "The grantor must take legal action to terminate the estate."
  },
  {
    id: 160,
    question: "What is life estate pur autre vie?",
    answer: "A life estate measured by the life of someone other than the holder.",
    category: "Forms of Ownership, Transfer & Recording",
    difficulty: "hard",
    explanation: "The estate continues until the measuring life ends."
  },
  {
    id: 161,
    question: "What is a remainder interest?",
    answer: "The right to own property after a life estate ends.",
    category: "Forms of Ownership, Transfer & Recording",
    difficulty: "hard",
    explanation: "Can be vested (certain) or contingent (conditional)."
  },
  {
    id: 162,
    question: "What is a reversion interest?",
    answer: "The right of the grantor to regain ownership after a life estate ends.",
    category: "Forms of Ownership, Transfer & Recording",
    difficulty: "hard",
    explanation: "Occurs when no remainder interest is created."
  },
  {
    id: 163,
    question: "What are the four unities required for joint tenancy?",
    answer: "Time, title, interest, and possession.",
    category: "Forms of Ownership, Transfer & Recording",
    difficulty: "hard",
    explanation: "All four unities must be present to create a valid joint tenancy."
  },
  {
    id: 164,
    question: "What is community property?",
    answer: "Property acquired during marriage that belongs to both spouses equally.",
    category: "Forms of Ownership, Transfer & Recording",
    difficulty: "medium",
    explanation: "Recognized in certain states, each spouse owns half of community property."
  },
  {
    id: 165,
    question: "What is separate property?",
    answer: "Property owned by one spouse before marriage or acquired by gift or inheritance.",
    category: "Forms of Ownership, Transfer & Recording",
    difficulty: "medium",
    explanation: "Not subject to division in community property states."
  },
  {
    id: 166,
    question: "What are the essential elements of a deed?",
    answer: "Grantor, grantee, consideration, property description, and signature.",
    category: "Forms of Ownership, Transfer & Recording",
    difficulty: "medium",
    explanation: "All elements must be present for a valid deed."
  },
  {
    id: 167,
    question: "What is a quitclaim deed?",
    answer: "A deed that transfers whatever interest the grantor has, without warranties.",
    category: "Forms of Ownership, Transfer & Recording",
    difficulty: "medium",
    explanation: "Provides no protection against title defects."
  },
  {
    id: 168,
    question: "What is a bargain and sale deed?",
    answer: "A deed that implies the grantor has title but provides no warranties.",
    category: "Forms of Ownership, Transfer & Recording",
    difficulty: "hard",
    explanation: "Common in some states, provides minimal protection."
  },
  {
    id: 169,
    question: "What is constructive notice?",
    answer: "Legal presumption that everyone knows what's recorded in public records.",
    category: "Forms of Ownership, Transfer & Recording",
    difficulty: "hard",
    explanation: "Means that recorded documents are considered known to all parties, regardless of actual knowledge."
  },
  {
    id: 170,
    question: "What is the recording act?",
    answer: "State law that determines priority of claims to property.",
    category: "Forms of Ownership, Transfer & Recording",
    difficulty: "hard",
    explanation: "Establishes rules for resolving competing claims to property."
  },
  {
    id: 171,
    question: "What is a race recording statute?",
    answer: "First to record wins, regardless of who had notice.",
    category: "Forms of Ownership, Transfer & Recording",
    difficulty: "hard",
    explanation: "Pure race statute - recording determines priority."
  },
  {
    id: 172,
    question: "What is a notice recording statute?",
    answer: "A bona fide purchaser without notice takes priority over unrecorded interests.",
    category: "Forms of Ownership, Transfer & Recording",
    difficulty: "hard",
    explanation: "Notice of prior unrecorded interest defeats later purchaser."
  },
  {
    id: 173,
    question: "What is a race-notice recording statute?",
    answer: "Must be first to record AND without notice of prior unrecorded interests.",
    category: "Forms of Ownership, Transfer & Recording",
    difficulty: "hard",
    explanation: "Combines elements of both race and notice statutes."
  },
  {
    id: 174,
    question: "What is a bona fide purchaser?",
    answer: "Someone who buys property in good faith for value without notice of defects.",
    category: "Forms of Ownership, Transfer & Recording",
    difficulty: "medium",
    explanation: "Protected by recording acts against prior unrecorded interests."
  },
  {
    id: 175,
    question: "What is a title commitment?",
    answer: "A preliminary report from a title company showing the current state of title, any exceptions to coverage, and requirements that must be met before issuing title insurance.",
    category: "Forms of Ownership, Transfer & Recording",
    difficulty: "medium",
    explanation: "The title commitment is issued after the title search and shows what the title company found, what they'll insure, and what conditions must be satisfied (like paying off liens) before closing can proceed."
  },
  {
    id: 176,
    question: "What is a quiet title action?",
    answer: "A lawsuit to establish clear ownership and remove clouds on title.",
    category: "Forms of Ownership, Transfer & Recording",
    difficulty: "hard",
    explanation: "Court proceeding to resolve title disputes and defects."
  },
  {
    id: 177,
    question: "What is adverse possession?",
    answer: "Acquiring title to property through continuous, hostile, open, and notorious use.",
    category: "Forms of Ownership, Transfer & Recording",
    difficulty: "hard",
    explanation: "Must meet all requirements for the statutory period."
  },
  {
    id: 178,
    question: "What is color of title?",
    answer: "A claim to property based on a defective deed or document.",
    category: "Forms of Ownership, Transfer & Recording",
    difficulty: "hard",
    explanation: "Can reduce the adverse possession period in some states."
  },
  {
    id: 179,
    question: "What is a covenant running with the land?",
    answer: "A covenant that binds future owners of the property.",
    category: "Forms of Ownership, Transfer & Recording",
    difficulty: "hard",
    explanation: "Must meet certain requirements to run with the land."
  },
  {
    id: 180,
    question: "What is a Superfund site?",
    answer: "A contaminated site designated for cleanup under federal environmental law.",
    category: "Property Disclosures & Environmental Issues",
    difficulty: "hard",
    explanation: "Also called CERCLA sites, can affect property values and insurability."
  },
  {
    id: 181,
    question: "What is CERCLA?",
    answer: "Comprehensive Environmental Response, Compensation, and Liability Act.",
    category: "Property Disclosures & Environmental Issues",
    difficulty: "hard",
    explanation: "Federal law that addresses hazardous waste cleanup and liability."
  },
  {
    id: 182,
    question: "What is RCRA?",
    answer: "Resource Conservation and Recovery Act.",
    category: "Property Disclosures & Environmental Issues",
    difficulty: "hard",
    explanation: "Federal law regulating hazardous waste generation, treatment, and disposal."
  },
  {
    id: 183,
    question: "What is a Phase I Environmental Assessment?",
    answer: "A preliminary investigation to identify potential environmental contamination.",
    category: "Property Disclosures & Environmental Issues",
    difficulty: "hard",
    explanation: "Required for commercial properties and some residential transactions."
  },
  {
    id: 184,
    question: "What is a Phase II Environmental Assessment?",
    answer: "Detailed testing and analysis if Phase I identifies potential contamination.",
    category: "Property Disclosures & Environmental Issues",
    difficulty: "hard",
    explanation: "Involves soil, water, and air testing to confirm contamination levels."
  },
  {
    id: 185,
    question: "What is a septic system?",
    answer: "An on-site wastewater treatment system for properties not connected to sewer.",
    category: "Property Disclosures & Environmental Issues",
    difficulty: "medium",
    explanation: "Requires regular maintenance and may need disclosure in some states."
  },
  {
    id: 186,
    question: "What is a UST (Underground Storage Tank)?",
    answer: "A tank buried underground used to store petroleum or hazardous substances.",
    category: "Property Disclosures & Environmental Issues",
    difficulty: "hard",
    explanation: "Subject to strict regulations and can cause environmental contamination."
  },
  {
    id: 187,
    question: "What is a brownfield?",
    answer: "A property that may have environmental contamination from previous use.",
    category: "Property Disclosures & Environmental Issues",
    difficulty: "hard",
    explanation: "May be eligible for cleanup incentives and redevelopment programs."
  },
  {
    id: 188,
    question: "What is an HOA?",
    answer: "Homeowners Association that enforces community rules and maintains common areas.",
    category: "Property Disclosures & Environmental Issues",
    difficulty: "easy",
    explanation: "Can impose fees and restrictions on property use."
  },
  {
    id: 189,
    question: "What is a special assessment?",
    answer: "An additional fee charged by an HOA for specific improvements or repairs.",
    category: "Property Disclosures & Environmental Issues",
    difficulty: "medium",
    explanation: "Can be a significant unexpected cost for property owners."
  },
  {
    id: 190,
    question: "What is a mechanic's lien?",
    answer: "A lien filed by contractors or suppliers for unpaid work or materials.",
    category: "Property Disclosures & Environmental Issues",
    difficulty: "medium",
    explanation: "Can be filed even if the property owner didn't directly hire the contractor."
  },
  {
    id: 191,
    question: "What is a lis pendens?",
    answer: "A notice filed in public records indicating pending litigation affecting property.",
    category: "Property Disclosures & Environmental Issues",
    difficulty: "hard",
    explanation: "Warns potential buyers that the property is subject to a lawsuit."
  },
  {
    id: 192,
    question: "What is a utility easement?",
    answer: "An easement allowing utility companies to install and maintain equipment.",
    category: "Property Disclosures & Environmental Issues",
    difficulty: "medium",
    explanation: "Common on most properties, allows access for power, water, sewer lines."
  },
  {
    id: 193,
    question: "What is a prescriptive easement?",
    answer: "An easement acquired through continuous use without permission.",
    category: "Property Disclosures & Environmental Issues",
    difficulty: "hard",
    explanation: "Similar to adverse possession but only grants use rights, not ownership."
  },
  {
    id: 194,
    question: "An applicant's Texas salesperson license is inactive. Which steps must be completed to activate it before practicing real estate?",
    answer: "Obtain broker sponsorship, complete any required CE/SAE, submit activation through TREC, and pay applicable fees.",
    category: "Property Disclosures & Environmental Issues",
    difficulty: "medium",
    explanation: "Texas license activation requires completing specific steps in the correct order. Understanding the process is crucial for licensees who need to reactivate inactive licenses."
  },
  {
    id: 195,
    question: "What is a non-conforming use?",
    answer: "A use that was legal when established but no longer conforms to current zoning.",
    category: "Property Disclosures & Environmental Issues",
    difficulty: "medium",
    explanation: "Generally allowed to continue but cannot be expanded or changed."
  },
  {
    id: 196,
    question: "What is a HELOC?",
    answer: "Home Equity Line of Credit - a revolving credit line secured by home equity.",
    category: "Financing & Settlement",
    difficulty: "medium",
    explanation: "Works like a credit card, borrower can draw funds as needed."
  },
  {
    id: 197,
    question: "What is PMI?",
    answer: "Private Mortgage Insurance - insurance that protects the lender if borrower defaults.",
    category: "Financing & Settlement",
    difficulty: "medium",
    explanation: "Required when down payment is less than 20% on conventional loans."
  },
  {
    id: 198,
    question: "What is a fixed-rate mortgage?",
    answer: "A mortgage with an interest rate that remains constant throughout the loan term.",
    category: "Financing & Settlement",
    difficulty: "easy",
    explanation: "Monthly payment remains the same, providing payment stability."
  },
  {
    id: 199,
    question: "What is a bridge loan?",
    answer: "A short-term loan to bridge the gap between buying and selling properties.",
    category: "Financing & Settlement",
    difficulty: "hard",
    explanation: "Helps buyers purchase new home before selling current home."
  },
  {
    id: 200,
    question: "What is a jumbo loan?",
    answer: "A loan that exceeds the conforming loan limits set by Fannie Mae and Freddie Mac.",
    category: "Financing & Settlement",
    difficulty: "medium",
    explanation: "Higher interest rates and stricter requirements than conforming loans."
  },
  {
    id: 201,
    question: "What is a conforming loan?",
    answer: "A loan that meets the size and credit requirements of Fannie Mae and Freddie Mac.",
    category: "Financing & Settlement",
    difficulty: "medium",
    explanation: "Easier to sell in secondary market, typically lower interest rates."
  },
  {
    id: 202,
    question: "What is a subprime loan?",
    answer: "A loan made to borrowers with poor credit or high debt-to-income ratios.",
    category: "Financing & Settlement",
    difficulty: "medium",
    explanation: "Higher interest rates and fees due to increased risk."
  },
  {
    id: 203,
    question: "What is a closing?",
    answer: "The final step in a real estate transaction where ownership is transferred.",
    category: "Financing & Settlement",
    difficulty: "easy",
    explanation: "All parties sign documents and funds are distributed."
  },
  {
    id: 204,
    question: "What is the difference between a HUD-1 and a Closing Disclosure?",
    answer: "HUD-1 was used for most transactions until 2015. Closing Disclosure is now required for most residential transactions and provides clearer, standardized information about loan terms and closing costs.",
    category: "Financing & Settlement",
    difficulty: "hard",
    explanation: "The Closing Disclosure replaced the HUD-1 for most residential mortgage transactions. It's required by the TILA-RESPA Integrated Disclosure (TRID) rule and must be provided to borrowers at least 3 days before closing."
  },
  {
    id: 205,
    question: "What are typical buyer closing costs in Texas?",
    answer: "Loan origination fees, title insurance, appraisal, survey, home inspection, recording fees, prepaid property taxes and insurance, and escrow deposits.",
    category: "Financing & Settlement",
    difficulty: "medium",
    explanation: "Buyer closing costs typically range from 2-5% of the loan amount. In Texas, buyers often pay for title insurance, survey, and their share of prorated property taxes. Some costs can be negotiated with the seller."
  },
  {
    id: 206,
    question: "What is escrow?",
    answer: "A neutral third party that holds funds and documents during a transaction.",
    category: "Financing & Settlement",
    difficulty: "easy",
    explanation: "Ensures all conditions are met before releasing funds and documents."
  },
  {
    id: 207,
    question: "What is a title company?",
    answer: "A company that handles title searches, insurance, and closing services.",
    category: "Financing & Settlement",
    difficulty: "easy",
    explanation: "Ensures clear title and facilitates the closing process."
  },
  {
    id: 208,
    question: "What is the difference between lender's title insurance and owner's title insurance?",
    answer: "Lender's title insurance protects the lender's interest up to the loan amount and is typically required. Owner's title insurance protects the buyer's equity and is optional but recommended.",
    category: "Financing & Settlement",
    difficulty: "medium",
    explanation: "Lender's policy protects the lender and decreases as the loan is paid down. Owner's policy protects the buyer's full purchase price and equity for as long as they own the property. In Texas, sellers often pay for owner's title insurance."
  },
  {
    id: 209,
    question: "What is a title defect and how can it affect a transaction?",
    answer: "A problem with the property's title such as liens, easements, or ownership disputes. It can delay or prevent closing until resolved.",
    category: "Financing & Settlement",
    difficulty: "medium",
    explanation: "Title defects must be cleared before closing. Common defects include unpaid liens, undisclosed easements, boundary disputes, or errors in previous deeds. The seller is typically responsible for clearing defects."
  },
  {
    id: 210,
    question: "How do you calculate the area of a rectangle?",
    answer: "Length × Width",
    category: "Math Calculations",
    difficulty: "easy",
    explanation: "To find the area of a rectangle, multiply the length by the width. Formula: Area = Length × Width. The result will be in square units (e.g., square feet, square yards). Example: A rectangular lot that is 100 feet long and 150 feet wide has an area of 100 × 150 = 15,000 square feet. This is the most basic area calculation used frequently in real estate for determining lot sizes, building footprints, and property descriptions."
  },
  {
    id: 211,
    question: "How do you calculate the area of a triangle?",
    answer: "(Base × Height) ÷ 2",
    category: "Math Calculations",
    difficulty: "easy",
    explanation: "To find the area of a triangle, multiply the base by the height, then divide by 2. Formula: Area = (Base × Height) ÷ 2. Important: The base and height must be perpendicular to each other (form a right angle). Example: A triangular lot with a base of 200 feet and height of 150 feet has an area of (200 × 150) ÷ 2 = 30,000 ÷ 2 = 15,000 square feet. This formula is used for calculating the area of irregularly shaped lots that can be divided into triangular sections."
  },
  {
    id: 212,
    question: "How do you calculate the area of a circle?",
    answer: "π × Radius²",
    category: "Math Calculations",
    difficulty: "medium",
    explanation: "To find the area of a circle, multiply pi (π, approximately 3.14159) by the radius squared. Formula: Area = π × r², where r is the radius (half the diameter). Step 1: Square the radius (multiply it by itself). Step 2: Multiply by π (3.14159). Example: A circular lot with a radius of 100 feet has an area of 3.14159 × (100)² = 3.14159 × 10,000 = 31,415.9 square feet. Note: If given the diameter, divide by 2 to get the radius first."
  },
  {
    id: 213,
    question: "How many square feet are in an acre?",
    answer: "43,560 square feet",
    category: "Math Calculations",
    difficulty: "easy",
    explanation: "One acre equals exactly 43,560 square feet. This is a standard measurement that must be memorized for real estate exams. To convert square feet to acres, divide by 43,560. Example: 87,120 square feet ÷ 43,560 = 2 acres. Useful conversions: 1 acre = 43,560 sq ft, 1 acre = 4,840 sq yards, 1 section = 640 acres, 1 township = 36 sections. This conversion is essential for determining lot sizes and property descriptions in Texas."
  },
  {
    id: 214,
    question: "How many square yards are in an acre?",
    answer: "4,840 square yards",
    category: "Math Calculations",
    difficulty: "medium",
    explanation: "One acre equals 4,840 square yards. To convert: Since 1 acre = 43,560 square feet, and 1 square yard = 9 square feet, divide 43,560 by 9: 43,560 ÷ 9 = 4,840 square yards. Alternatively, you can convert square feet to square yards by dividing by 9. Example: To convert 43,560 sq ft to sq yards: 43,560 ÷ 9 = 4,840 sq yards. This conversion is useful when working with carpeting, flooring, or other materials sold by the square yard."
  },
  {
    id: 215,
    question: "How do you calculate commission?",
    answer: "Sale Price × Commission Rate",
    category: "Math Calculations",
    difficulty: "easy",
    explanation: "Total commission is calculated by multiplying the sale price by the commission rate (as a decimal). Formula: Commission = Sale Price × Commission Rate. Step 1: Convert the commission rate percentage to a decimal (e.g., 6% = 0.06, 5.5% = 0.055). Step 2: Multiply the sale price by the decimal rate. Example: A property sells for $400,000 with a 6% commission rate: $400,000 × 0.06 = $24,000 total commission. The commission rate is always negotiable and is typically paid by the seller at closing. If the commission is split between listing and selling brokers, each receives a portion."
  },
  {
    id: 216,
    question: "How do you calculate net listing commission?",
    answer: "Sale Price - Net Amount to Seller",
    category: "Math Calculations",
    difficulty: "medium",
    explanation: "In a net listing, the broker's commission is the difference between the sale price and the seller's net amount. Formula: Commission = Sale Price - Net Amount to Seller. The broker keeps any amount above the seller's net price. Example: If a seller wants to net $250,000 and the property sells for $275,000, the broker's commission is $275,000 - $250,000 = $25,000. Important: Net listings are illegal in many states (including Texas) because they create a conflict of interest where the broker benefits from a higher sale price while the seller may not. Always check state regulations."
  },
  {
    id: 217,
    question: "How do you calculate prorated property taxes?",
    answer: "(Annual Tax ÷ 365) × Days Owned",
    category: "Math Calculations",
    difficulty: "medium",
    explanation: "Proration divides expenses between buyer and seller based on ownership days. Texas typically uses a 365-day year. Formula: Prorated Amount = (Annual Tax ÷ 365) × Days Owned. Step 1: Calculate the daily tax rate by dividing the annual tax by 365. Step 2: Multiply the daily rate by the number of days each party owned the property. The seller is debited (owes) for the days they owned the property, and the buyer is credited (receives) for those same days since they'll be responsible going forward. Example: Annual taxes of $3,650, seller owned for 90 days: ($3,650 ÷ 365) × 90 = $10 × 90 = $900. Always count the closing day as belonging to the buyer."
  },
  {
    id: 218,
    question: "How do you calculate prorated rent?",
    answer: "(Monthly Rent ÷ 30) × Days Occupied",
    category: "Math Calculations",
    difficulty: "medium",
    explanation: "Prorated rent divides the monthly rent based on the number of days occupied. Formula: Prorated Rent = (Monthly Rent ÷ 30) × Days Occupied. Step 1: Calculate the daily rent by dividing the monthly rent by 30. Step 2: Multiply the daily rent by the number of days occupied. This calculation is commonly used when a tenant moves in or out mid-month, or when closing occurs mid-month and rent needs to be prorated between buyer and seller. Example: Monthly rent of $1,500, tenant occupies for 12 days: ($1,500 ÷ 30) × 12 = $50 × 12 = $600. Note: Some calculations use the actual number of days in the month, but 30 days is standard for simplicity."
  },
  {
    id: 219,
    question: "How do you calculate loan-to-value ratio?",
    answer: "Loan Amount ÷ Property Value",
    category: "Math Calculations",
    difficulty: "easy",
    explanation: "LTV (Loan-to-Value) ratio shows what percentage of the property value is being financed. Formula: LTV = Loan Amount ÷ Property Value. Step 1: Divide the loan amount by the property's appraised value or purchase price (whichever is lower). Step 2: Convert to percentage by multiplying by 100. Example: A $400,000 property with a $320,000 loan: $320,000 ÷ $400,000 = 0.80 = 80% LTV. This means the lender will finance 80%, and the borrower must provide a 20% down payment ($80,000). Lenders use LTV to assess risk - higher LTV ratios mean higher risk and may require mortgage insurance."
  },
  {
    id: 220,
    question: "How do you calculate debt-to-income ratio?",
    answer: "Monthly Debt Payments ÷ Monthly Gross Income",
    category: "Math Calculations",
    difficulty: "easy",
    explanation: "Debt-to-income (DTI) ratio measures a borrower's ability to manage monthly debt payments. Formula: DTI = Monthly Debt Payments ÷ Monthly Gross Income. Step 1: Add up all monthly debt payments (mortgage, car loans, credit cards, student loans, etc.). Step 2: Divide by monthly gross income (before taxes). Step 3: Convert to percentage by multiplying by 100. Example: Monthly debts of $2,000 and monthly gross income of $6,000: $2,000 ÷ $6,000 = 0.333 = 33.3% DTI. Most lenders prefer DTI ratios below 43%, with 36% or lower being ideal. This ratio helps lenders assess the borrower's ability to repay the loan."
  },
  {
    id: 221,
    question: "How do you calculate monthly mortgage payment?",
    answer: "Use the mortgage payment formula: M = P[r(1+r)^n]/[(1+r)^n-1]",
    category: "Math Calculations",
    difficulty: "hard",
    explanation: "The monthly mortgage payment formula calculates principal and interest for an amortized loan. Formula: M = P[r(1+r)^n]/[(1+r)^n-1], where M = monthly payment, P = principal (loan amount), r = monthly interest rate (annual rate ÷ 12), n = number of payments (loan term in years × 12). Step 1: Convert annual interest rate to monthly: divide by 12. Step 2: Calculate (1+r)^n. Step 3: Use the full formula. Example: $300,000 loan at 5% for 30 years: r = 0.05/12 = 0.004167, n = 360, M ≈ $1,610.46. Note: On real estate exams, you'll typically be given an amortization factor table rather than calculating this manually. The factor is multiplied by the loan amount (in thousands) to get the monthly payment."
  },
  {
    id: 222,
    question: "How do you calculate interest on a loan?",
    answer: "Principal × Interest Rate × Time",
    category: "Math Calculations",
    difficulty: "easy",
    explanation: "Simple interest is calculated by multiplying the principal (loan amount) by the interest rate and time period. Formula: Interest = Principal × Interest Rate × Time. Step 1: Convert the interest rate to a decimal. Step 2: Ensure time period matches the rate (if annual rate, use years; if monthly rate, use months). Example: $200,000 loan at 5% annual interest for 1 year: $200,000 × 0.05 × 1 = $10,000 annual interest. For monthly interest: $200,000 × (0.05 ÷ 12) × 1 = $833.33. Note: On amortized loans, the interest portion decreases over time as principal is paid down, so this formula gives you the interest for a specific period when the principal remains constant."
  },
  {
    id: 223,
    question: "How do you calculate capitalization rate?",
    answer: "Net Operating Income ÷ Property Value",
    category: "Math Calculations",
    difficulty: "medium",
    explanation: "The capitalization (cap) rate measures the rate of return on an income-producing property. Formula: Cap Rate = NOI ÷ Property Value. Step 1: Calculate NOI (Net Operating Income) = Gross Income - Operating Expenses (but before debt service and taxes). Step 2: Divide NOI by the property's value or sale price. Step 3: Convert to percentage. Example: Property with NOI of $50,000 and value of $625,000: $50,000 ÷ $625,000 = 0.08 = 8% cap rate. A higher cap rate indicates higher risk or lower value; a lower cap rate indicates lower risk or higher value. This is a key metric for evaluating investment properties and is used in the income approach to valuation."
  },
  {
    id: 224,
    question: "How do you calculate gross rent multiplier?",
    answer: "Property Price ÷ Gross Annual Rent",
    category: "Math Calculations",
    difficulty: "medium",
    explanation: "GRM (Gross Rent Multiplier) is a simple valuation method for residential properties (typically 1-4 units). Formula: GRM = Property Price ÷ Gross Annual Rent. Step 1: Divide the property's sale price by its gross annual rental income. Example: A property sold for $400,000 with annual rent of $40,000: $400,000 ÷ $40,000 = 10 GRM. To estimate value using GRM: Value = GRM × Gross Annual Rent. This method is quick but less accurate than the cap rate method because it doesn't account for operating expenses, vacancies, or debt service. It's useful for quick estimates and comparing similar properties in the same market area."
  },
  {
    id: 225,
    question: "How do you calculate net operating income?",
    answer: "Gross Income - Operating Expenses",
    category: "Math Calculations",
    difficulty: "easy",
    explanation: "NOI (Net Operating Income) is the income remaining after all operating expenses but before debt service (mortgage payments) and income taxes. Formula: NOI = Gross Income - Operating Expenses. Step 1: Calculate gross income (total rental income, including all units). Step 2: Subtract all operating expenses (property management, maintenance, insurance, property taxes, utilities, repairs, etc.). Important: Do NOT subtract mortgage payments or income taxes - those come after NOI. Example: Gross income of $120,000 and operating expenses of $45,000: $120,000 - $45,000 = $75,000 NOI. NOI is a key metric for income-producing properties and is used in the income approach to valuation and cap rate calculations."
  },
  {
    id: 226,
    question: "How do you calculate effective gross income?",
    answer: "Potential Gross Income - Vacancy and Collection Losses",
    category: "Math Calculations",
    difficulty: "medium",
    explanation: "Effective Gross Income (EGI) is the actual income a property generates after accounting for vacancies and uncollectible rent. Formula: EGI = Potential Gross Income (PGI) - Vacancy and Collection Losses. Step 1: Calculate PGI (the total income if all units were rented at market rates). Step 2: Subtract vacancy losses (units not rented) and collection losses (unpaid rent). Example: PGI of $100,000 with 5% vacancy rate: $100,000 - ($100,000 × 0.05) = $100,000 - $5,000 = $95,000 EGI. Alternatively: EGI = PGI × (1 - Vacancy Rate). EGI is used to calculate NOI: NOI = EGI - Operating Expenses. This reflects realistic income potential rather than theoretical maximum."
  },
  {
    id: 227,
    question: "How do you calculate depreciation for tax purposes?",
    answer: "Property Value ÷ Depreciation Period",
    category: "Math Calculations",
    difficulty: "medium",
    explanation: "Depreciation for tax purposes allows investors to deduct the cost of the property over time. Formula: Annual Depreciation = Property Value ÷ Depreciation Period. Step 1: Determine the depreciable basis (typically the property's cost basis, excluding land value since land doesn't depreciate). Step 2: Divide by the depreciation period. For residential rental property: 27.5 years. For commercial property: 39 years. Example: A $275,000 residential rental property (excluding $50,000 land value): Depreciable basis = $225,000, Annual depreciation = $225,000 ÷ 27.5 = $8,181.82 per year. This is a non-cash expense that reduces taxable income, providing tax benefits to real estate investors."
  },
  {
    id: 228,
    question: "How do you calculate property tax?",
    answer: "Assessed Value × Tax Rate",
    category: "Math Calculations",
    difficulty: "easy",
    explanation: "Property taxes are calculated by multiplying the assessed value by the tax rate. Formula: Annual Property Tax = Assessed Value × Tax Rate. Step 1: Determine the assessed value (set by the county appraisal district, typically based on market value). Step 2: Convert the tax rate to a decimal (e.g., 2.1% = 0.021). Step 3: Multiply assessed value by tax rate. Example: Assessed value of $300,000 with a 2.1% tax rate: $300,000 × 0.021 = $6,300 annual property tax. In Texas, property tax rates vary by county, city, and school district, and can be higher than many other states. The assessed value is typically based on the property's market value as determined by the county appraisal district."
  },
  {
    id: 229,
    question: "How do you calculate transfer tax?",
    answer: "Sale Price × Transfer Tax Rate",
    category: "Math Calculations",
    difficulty: "easy",
    explanation: "Transfer tax (or transfer fee) is calculated by multiplying the sale price by the transfer tax rate. Formula: Transfer Tax = Sale Price × Transfer Tax Rate. Step 1: Convert the tax rate to a decimal. Step 2: Multiply the sale price by the rate. Important: Texas does NOT have a state transfer tax, but some counties may charge transfer fees. These fees vary by county and are typically much lower than states with transfer taxes. Example: In a county with a 0.1% transfer fee on a $500,000 sale: $500,000 × 0.001 = $500. Transfer taxes/fees are usually paid by the seller at closing, though this can be negotiated. Always check local regulations as rates and rules vary significantly."
  },
  {
    id: 230,
    question: "How do you calculate points on a loan?",
    answer: "Loan Amount × Point Percentage",
    category: "Math Calculations",
    difficulty: "medium",
    explanation: "Points are fees paid to the lender, where 1 point equals 1% of the loan amount. Formula: Points Cost = Loan Amount × (Number of Points ÷ 100). Step 1: Convert points to a percentage (1 point = 1% = 0.01, 2 points = 2% = 0.02). Step 2: Multiply the loan amount by the decimal. Example: 2 points on a $300,000 loan: $300,000 × 0.02 = $6,000. Points are typically used to 'buy down' the interest rate (discount points) or as an origination fee. Discount points reduce the interest rate, while origination points are fees for processing the loan. Points are usually paid at closing and may be tax-deductible."
  },
  {
    id: 231,
    question: "How do you calculate closing costs?",
    answer: "Sum of all fees and charges at closing",
    category: "Math Calculations",
    difficulty: "easy",
    explanation: "Closing costs are the total of all fees, taxes, and charges paid at closing. Formula: Closing Costs = Sum of all fees and charges. Common closing costs include: loan origination fees, appraisal fees, title insurance, escrow fees, recording fees, transfer taxes/fees, property taxes (prorated), homeowners insurance, prepaid interest, and points. Step 1: List all fees and charges. Step 2: Add them together. Example: Origination fee ($2,000) + Appraisal ($500) + Title insurance ($1,200) + Recording fees ($200) + Transfer fee ($500) = $4,400 total closing costs. Closing costs typically range from 2-5% of the loan amount. Buyers and sellers each pay different closing costs, which are itemized on the Closing Disclosure (CD) or HUD-1 Settlement Statement."
  },
  {
    id: 232,
    question: "How do you calculate seller's net proceeds?",
    answer: "Sale Price - (Mortgage Balance + Closing Costs + Commission)",
    category: "Math Calculations",
    difficulty: "medium",
    explanation: "Seller's net proceeds are the amount the seller receives after all deductions. Formula: Net Proceeds = Sale Price - (Mortgage Balance + Closing Costs + Commission + Other Costs). Step 1: Start with the sale price. Step 2: Subtract the mortgage payoff (remaining loan balance). Step 3: Subtract closing costs (seller's portion: transfer fees, title insurance, prorated property taxes, etc.). Step 4: Subtract the real estate commission. Step 5: Subtract any other costs (repairs, concessions, etc.). Example: Sale price $400,000 - Mortgage $250,000 - Closing costs $5,000 - Commission $24,000 = $121,000 net proceeds. This is the actual cash the seller receives at closing."
  },
  {
    id: 233,
    question: "How do you calculate buyer's total cash needed at closing?",
    answer: "Down Payment + Closing Costs",
    category: "Math Calculations",
    difficulty: "easy",
    explanation: "Buyer's total cash needed at closing is the amount of cash the buyer must bring to closing. Formula: Total Cash Needed = Down Payment + Closing Costs. Important: The down payment is part of the purchase price, not in addition to it. The purchase price is financed through the mortgage, so the buyer only pays the down payment and closing costs in cash. Step 1: Calculate the down payment (typically a percentage of purchase price, e.g., 20% = 0.20). Step 2: Add closing costs (loan fees, appraisal, title insurance, prepaid items, etc.). Example: $400,000 purchase with 20% down payment ($80,000) and $8,000 closing costs: Total cash needed = $80,000 + $8,000 = $88,000. The remaining $320,000 is financed through the mortgage."
  },
  {
    id: 234,
    question: "How do you calculate appreciation?",
    answer: "(Current Value - Original Value) ÷ Original Value × 100",
    category: "Math Calculations",
    difficulty: "medium",
    explanation: "Appreciation is the percentage increase in property value over time. Formula: Appreciation % = [(Current Value - Original Value) ÷ Original Value] × 100. Step 1: Calculate the dollar amount of appreciation: Current Value - Original Value. Step 2: Divide by the original value. Step 3: Multiply by 100 to convert to percentage. Example: Property purchased for $300,000, now worth $360,000: [($360,000 - $300,000) ÷ $300,000] × 100 = ($60,000 ÷ $300,000) × 100 = 0.20 × 100 = 20% appreciation. To find annual appreciation, divide the total appreciation percentage by the number of years. Appreciation can occur due to market conditions, improvements, or inflation."
  },
  {
    id: 235,
    question: "How do you calculate depreciation?",
    answer: "(Original Value - Current Value) ÷ Original Value × 100",
    category: "Math Calculations",
    difficulty: "medium",
    explanation: "Depreciation (in appraisal terms) is the percentage decrease in property value over time. Formula: Depreciation % = [(Original Value - Current Value) ÷ Original Value] × 100. Step 1: Calculate the dollar amount of depreciation: Original Value - Current Value. Step 2: Divide by the original value. Step 3: Multiply by 100 to convert to percentage. Example: Property originally worth $400,000, now worth $320,000: [($400,000 - $320,000) ÷ $400,000] × 100 = ($80,000 ÷ $400,000) × 100 = 0.20 × 100 = 20% depreciation. Note: This is different from tax depreciation, which is a deduction for tax purposes. Appraisal depreciation reflects actual loss in value due to wear, age, or obsolescence."
  },
  {
    id: 236,
    question: "How do you calculate return on investment (ROI)?",
    answer: "(Net Profit ÷ Investment) × 100",
    category: "Math Calculations",
    difficulty: "medium",
    explanation: "ROI (Return on Investment) measures the percentage return on the total investment. Formula: ROI = (Net Profit ÷ Total Investment) × 100. Step 1: Calculate net profit (sale price - purchase price - all costs + income received). Step 2: Divide by the total investment (purchase price + improvements + other costs). Step 3: Multiply by 100 to convert to percentage. Example: Property purchased for $200,000, sold for $250,000, with $10,000 in improvements and $5,000 in selling costs, and $20,000 in rental income received: Net profit = $250,000 - $200,000 - $10,000 - $5,000 + $20,000 = $55,000. Total investment = $200,000 + $10,000 = $210,000. ROI = ($55,000 ÷ $210,000) × 100 = 26.2%. This shows the overall return on the investment."
  },
  {
    id: 237,
    question: "How do you calculate cash-on-cash return?",
    answer: "(Annual Cash Flow ÷ Cash Invested) × 100",
    category: "Math Calculations",
    difficulty: "hard",
    explanation: "Cash-on-cash return measures the annual return on the actual cash invested (down payment), not the total property value. Formula: Cash-on-Cash Return = (Annual Cash Flow ÷ Cash Invested) × 100. Step 1: Calculate cash invested (down payment + closing costs + any improvements paid in cash). Step 2: Calculate annual cash flow (NOI - Debt Service, which is income after operating expenses and mortgage payments). Step 3: Divide cash flow by cash invested and multiply by 100. Example: $200,000 down payment, $15,000 annual cash flow: ($15,000 ÷ $200,000) × 100 = 7.5% cash-on-cash return. This metric shows how well the investor's actual cash is performing, making it a key metric for evaluating leveraged investment properties. It's different from ROI, which considers the total property value."
  },
  {
    id: 238,
    question: "How do you calculate break-even ratio?",
    answer: "(Operating Expenses + Debt Service) ÷ Gross Income",
    category: "Math Calculations",
    difficulty: "hard",
    explanation: "Break-even ratio shows what percentage of gross income is needed to cover all expenses. Formula: Break-Even Ratio = (Operating Expenses + Debt Service) ÷ Gross Income. Step 1: Add operating expenses (maintenance, insurance, property taxes, management, etc.) and debt service (mortgage payments). Step 2: Divide by gross income (total rental income). Step 3: Convert to percentage. Example: Operating expenses $40,000 + Debt service $30,000 = $70,000 total expenses. Gross income $100,000: $70,000 ÷ $100,000 = 0.70 = 70% break-even ratio. This means 70% of income goes to expenses, leaving 30% as potential cash flow. A lower break-even ratio is better - it means less income is needed to cover expenses. Lenders often require break-even ratios below 85%."
  },
  {
    id: 239,
    question: "How do you calculate vacancy rate?",
    answer: "(Vacant Units ÷ Total Units) × 100",
    category: "Math Calculations",
    difficulty: "easy",
    explanation: "Vacancy rate shows what percentage of units are unoccupied. Formula: Vacancy Rate = (Vacant Units ÷ Total Units) × 100. Step 1: Count the number of vacant units. Step 2: Divide by the total number of units. Step 3: Multiply by 100 to convert to percentage. Example: A 20-unit building with 3 vacant units: (3 ÷ 20) × 100 = 0.15 × 100 = 15% vacancy rate. This means 15% of the units are vacant. A lower vacancy rate is better - it indicates strong demand and higher income potential. Vacancy rates are used to calculate effective gross income and to assess market conditions. Typical vacancy rates vary by market and property type."
  },
  {
    id: 240,
    question: "How do you calculate absorption rate?",
    answer: "Units Sold ÷ Time Period",
    category: "Math Calculations",
    difficulty: "medium",
    explanation: "Absorption rate measures how quickly properties are selling in a market. Formula: Absorption Rate = Units Sold ÷ Time Period. Step 1: Count the number of units sold in a specific time period (usually months). Step 2: Divide by the number of months in that period. Example: 120 homes sold in the past 6 months: 120 ÷ 6 = 20 homes per month absorption rate. This helps determine market conditions: High absorption rate = strong seller's market (properties selling quickly). Low absorption rate = buyer's market (properties taking longer to sell). Absorption rate can also be calculated as a percentage: (Units Sold ÷ Total Available Units) × 100. This metric helps investors and agents understand market dynamics and pricing trends."
  },
  {
    id: 241,
    question: "How do you calculate price per square foot?",
    answer: "Sale Price ÷ Square Footage",
    category: "Math Calculations",
    difficulty: "easy",
    explanation: "Price per square foot is a common way to compare properties. Formula: Price per Square Foot = Sale Price ÷ Total Square Footage. Step 1: Divide the sale price by the total square footage of the property. Example: A property sold for $400,000 with 2,000 square feet: $400,000 ÷ 2,000 = $200 per square foot. This metric is commonly used in commercial real estate and helps compare properties of different sizes. To estimate value using price per square foot: Value = Price per Square Foot × Square Footage. Note: Price per square foot can vary significantly based on location, condition, and property type, so it's most useful when comparing similar properties in the same market area."
  },
  {
    id: 242,
    question: "How do you calculate rent per square foot?",
    answer: "Monthly Rent ÷ Square Footage",
    category: "Math Calculations",
    difficulty: "easy",
    explanation: "Rent per square foot is used to compare rental rates. Formula: Rent per Square Foot = Monthly Rent ÷ Square Footage. Step 1: Divide the monthly rent by the total square footage. Example: Monthly rent of $3,000 for a 1,500 sq ft unit: $3,000 ÷ 1,500 = $2.00 per square foot per month. Note: Commercial leases often quote rent per square foot per year, so you may need to convert. If annual rent is $36 per sq ft, monthly is $36 ÷ 12 = $3 per sq ft per month. This metric helps compare rental rates across different properties and is essential for commercial real estate analysis. It's also used to estimate rental income when you know the square footage and market rates."
  },
  {
    id: 243,
    question: "How do you calculate lot coverage ratio?",
    answer: "Building Footprint ÷ Lot Area",
    category: "Math Calculations",
    difficulty: "medium",
    explanation: "Lot coverage ratio shows what percentage of the lot is covered by buildings. Formula: Lot Coverage Ratio = Building Footprint ÷ Lot Area. Step 1: Calculate the building footprint (the area the building covers on the ground, viewed from above). Step 2: Divide by the total lot area. Step 3: Convert to percentage. Example: A building footprint of 2,000 sq ft on a 5,000 sq ft lot: 2,000 ÷ 5,000 = 0.40 = 40% lot coverage. This means 40% of the lot is covered by the building, leaving 60% as open space. Zoning codes often set maximum lot coverage ratios (e.g., 30-50%) to ensure adequate open space, parking, and setbacks. This is different from Floor Area Ratio (FAR), which includes all floors."
  },
  {
    id: 244,
    question: "How do you calculate floor area ratio (FAR)?",
    answer: "Total Building Area ÷ Lot Area",
    category: "Math Calculations",
    difficulty: "medium",
    explanation: "Floor Area Ratio (FAR) measures the total building area relative to the lot size, including all floors. Formula: FAR = Total Building Area ÷ Lot Area. Step 1: Calculate total building area (sum of all floor areas - if a building has 3 floors of 2,000 sq ft each, total = 6,000 sq ft). Step 2: Divide by the lot area. Example: A building with 10,000 sq ft total floor area on a 5,000 sq ft lot: 10,000 ÷ 5,000 = 2.0 FAR. This means the building has 2 times the lot area in total floor space. FAR is used in zoning to control building density. A higher FAR allows more building area (taller or larger buildings). Zoning codes set maximum FAR limits (e.g., 0.5, 1.0, 2.0) to control development intensity. FAR is different from lot coverage, which only looks at the ground footprint."
  },
  {
    id: 245,
    question: "How do you calculate frontage?",
    answer: "Length of property along the street",
    category: "Math Calculations",
    difficulty: "easy",
    explanation: "Frontage is the linear measurement of the property along a street, road, or waterway. Formula: Frontage = Length of property boundary along the street. Step 1: Measure the length of the property line that faces and borders the street. This is a linear measurement (in feet or meters), not an area. Example: A rectangular lot that is 100 feet wide along the street has 100 feet of frontage. Frontage is important for commercial properties because more frontage typically means more visibility and access, which can increase value. Properties with water frontage (lake, river, ocean) are also valued based on the length of waterfront. Frontage is used in zoning calculations and can affect property value, especially for retail and commercial properties where street visibility matters."
  },
  {
    id: 246,
    question: "A property sold for $425,000. The commission rate is 6%. What is the total commission?",
    answer: "$25,500",
    category: "Math Calculations",
    difficulty: "easy",
    explanation: "Total commission = Sales Price × Commission Rate (as a decimal). Step 1: Convert 6% to a decimal: 6% = 0.06. Step 2: Multiply the sales price by the commission rate: $425,000 × 0.06 = $25,500. The commission rate is always negotiable and is typically paid by the seller at closing. If the commission is split between listing and selling brokers, each would receive a portion of the $25,500."
  },
  {
    id: 247,
    question: "If a property is valued at $450,000 and a lender requires an 80% LTV, what is the loan amount?",
    answer: "$360,000",
    category: "Math Calculations",
    difficulty: "easy",
    explanation: "LTV (Loan-to-Value) ratio shows what percentage of the property value can be financed. Step 1: Convert 80% LTV to a decimal: 0.80. Step 2: Multiply the property value by the LTV ratio: $450,000 × 0.80 = $360,000. This means the lender will finance $360,000, and the borrower must provide a down payment of $90,000 (20% of $450,000)."
  },
  {
    id: 248,
    question: "A borrower is paying 2 discount points on a $350,000 loan. How much will the points cost?",
    answer: "$7,000",
    category: "Math Calculations",
    difficulty: "easy",
    explanation: "One point equals 1% of the loan amount. Step 1: Convert 2 points to a decimal: 2 points = 2% = 0.02. Step 2: Multiply the loan amount by the decimal: $350,000 × 0.02 = $7,000. Points are fees paid to the lender at closing, typically used to 'buy down' the interest rate (discount points) or as an origination fee. One point on a $350,000 loan = $3,500, so 2 points = $7,000."
  },
  {
    id: 249,
    question: "Annual property taxes are $4,800 and closing is on July 15th. The seller owned the property for 196 days. What is the seller's prorated share? (365-day year).",
    answer: "$2,580.82",
    category: "Math Calculations",
    difficulty: "medium",
    explanation: "Proration divides expenses between buyer and seller based on ownership days. Texas typically uses a 365-day year. Step 1: Calculate daily rate: $4,800 ÷ 365 = $13.15 per day. Step 2: The seller owned the property for 196 days. Step 3: Multiply daily rate by seller's days: $13.15 × 196 = $2,577.40, or using exact calculation: ($4,800 ÷ 365) × 196 = $2,580.82. The seller is debited (owes) $2,580.82, and the buyer is credited (receives) $2,580.82 since they'll be responsible for the remaining 169 days of the year."
  },
  {
    id: 250,
    question: "A property has an NOI of $55,000 and the market cap rate is 6.5%. What is the estimated value?",
    answer: "$846,153.85",
    category: "Math Calculations",
    difficulty: "medium",
    explanation: "The capitalization (cap) rate method estimates property value using the income approach. Formula: Value = NOI ÷ Cap Rate. NOI (Net Operating Income) is income after operating expenses but before debt service and taxes. Step 1: Convert 6.5% cap rate to a decimal: 0.065. Step 2: Divide NOI by cap rate: $55,000 ÷ 0.065 = $846,153.85. A higher cap rate indicates higher risk or lower value; a lower cap rate indicates lower risk or higher value. This method is commonly used for income-producing properties in Texas."
  },
  {
    id: 251,
    question: "A property has a gross annual rent of $78,000 and the area GRM is 11. What is the estimated value?",
    answer: "$858,000",
    category: "Math Calculations",
    difficulty: "medium",
    explanation: "GRM (Gross Rent Multiplier) is a simple valuation method for residential properties (1-4 units). Formula: Value = GRM × Gross Annual Rent. The GRM is found by comparing similar properties: GRM = Sales Price ÷ Gross Annual Rent. Step 1: Multiply the GRM by the gross annual rent: 11 × $78,000 = $858,000. This method is quick but less accurate than the cap rate method because it doesn't account for operating expenses. It's useful for quick estimates and comparing similar properties in the same Texas market area."
  },
  {
    id: 252,
    question: "A rectangular lot measures 200 feet by 300 feet. How many acres is it?",
    answer: "~1.38 acres",
    category: "Math Calculations",
    difficulty: "easy",
    explanation: "Step 1: Calculate the area in square feet: 200 feet × 300 feet = 60,000 square feet. Step 2: Convert square feet to acres by dividing by 43,560 (the number of square feet in one acre): 60,000 ÷ 43,560 = 1.377 acres, which rounds to approximately 1.38 acres. Formula: Area (sq ft) ÷ 43,560 = Acres. This calculation is essential for determining lot sizes and property descriptions in Texas."
  },
  {
    id: 253,
    question: "A buyer's PITI is $2,500/mo. Annual taxes are $4,200 and insurance is $1,800. What is the monthly P&I?",
    answer: "$2,000",
    category: "Math Calculations",
    difficulty: "medium",
    explanation: "PITI stands for Principal, Interest, Taxes, and Insurance - the total monthly mortgage payment. To find P&I (Principal and Interest only), subtract the monthly Taxes and Insurance from PITI. Step 1: Calculate monthly Taxes and Insurance: ($4,200 + $1,800) ÷ 12 = $6,000 ÷ 12 = $500 per month. Step 2: Subtract monthly T&I from PITI: $2,500 - $500 = $2,000. So the monthly Principal and Interest payment is $2,000. This is the portion that goes toward paying down the loan and interest."
  },
  {
    id: 254,
    question: "A property's assessed value is $380,000. The local tax rate is 2.1%. What are the annual property taxes?",
    answer: "$7,980",
    category: "Math Calculations",
    difficulty: "easy",
    explanation: "Property taxes in Texas are calculated by multiplying the assessed value by the tax rate. Step 1: Convert 2.1% to a decimal: 2.1% = 0.021. Step 2: Multiply assessed value by tax rate: $380,000 × 0.021 = $7,980. Texas property tax rates vary by county and school district, and can be higher than many other states. The assessed value is typically based on the property's market value as determined by the county appraisal district."
  },
  {
    id: 255,
    question: "A seller wants to net $280,000 after paying a 5% commission. What is the minimum selling price?",
    answer: "$294,736.84",
    category: "Math Calculations",
    difficulty: "hard",
    explanation: "This is a reverse commission calculation. If the seller nets $280,000 after a 5% commission, the commission comes out of the sale price, so the seller keeps 95% (100% - 5%). Step 1: Calculate what percentage the seller keeps: 100% - 5% = 95%, or 0.95 as a decimal. Step 2: Divide the desired net amount by the percentage kept: $280,000 ÷ 0.95 = $294,736.84. To verify: $294,736.84 × 0.05 = $14,736.84 commission, leaving $294,736.84 - $14,736.84 = $280,000 net. Formula: Minimum Sale Price = Desired Net ÷ (1 - Commission Rate)."
  },
  {
    id: 256,
    question: "A loan of $400,000 has a monthly P&I payment of $2,400. The annual interest rate is 5.25%. How much of the first month's payment is interest?",
    answer: "$1,750.00",
    category: "Math Calculations",
    difficulty: "medium",
    explanation: "On an amortized loan, early payments consist mostly of interest. To find the first month's interest, calculate the annual interest and divide by 12. Step 1: Calculate annual interest: $400,000 × 0.0525 (5.25% as decimal) = $21,000 per year. Step 2: Calculate monthly interest: $21,000 ÷ 12 = $1,750.00. The monthly P&I payment of $2,400 is not needed for this calculation - only the loan balance and interest rate matter. In the first month, $1,750.00 goes to interest and $650.00 goes to principal ($2,400 - $1,750.00 = $650.00). As the loan is paid down, the interest portion decreases and the principal portion increases."
  },
  {
    id: 257,
    question: "A property has PGI of $125,000, vacancy loss of 7%, and operating expenses of $48,000. What is the NOI?",
    answer: "$68,250",
    category: "Math Calculations",
    difficulty: "medium",
    explanation: "NOI (Net Operating Income) is calculated from PGI (Potential Gross Income) by accounting for vacancies and operating expenses. Step 1: Calculate EGI (Effective Gross Income): PGI minus vacancy loss. Vacancy loss = $125,000 × 0.07 = $8,750. EGI = $125,000 - $8,750 = $116,250. Alternatively: EGI = PGI × (1 - Vacancy Rate) = $125,000 × 0.93 = $116,250. Step 2: Calculate NOI: EGI minus Operating Expenses = $116,250 - $48,000 = $68,250. NOI is the income remaining after all operating expenses but before debt service (mortgage payments) and income taxes. It's a key metric for income-producing properties."
  },
  {
    id: 258,
    question: "A property's market value is $550,000. The owner has a loan balance of $385,000. What is the owner's equity?",
    answer: "$165,000",
    category: "Math Calculations",
    difficulty: "easy",
    explanation: "Equity represents the owner's actual interest in the property after subtracting debts. Formula: Equity = Market Value - Loan Balance. Step 1: Subtract the loan balance from the market value: $550,000 - $385,000 = $165,000. This is the amount the owner would receive if they sold the property and paid off the loan. Equity increases as the loan is paid down (amortization) or as the property value increases (appreciation)."
  },
  {
    id: 259,
    question: "An investor buys a property for $750,000 with a 25% down payment. The property NOI is $52,500 and annual debt service is $38,000. What is the cash-on-cash return?",
    answer: "7.7%",
    category: "Math Calculations",
    difficulty: "hard",
    explanation: "Cash-on-cash return measures the annual return on the actual cash invested (down payment). Formula: Cash-on-Cash Return = Annual Cash Flow ÷ Cash Invested. Step 1: Calculate the down payment (cash invested): $750,000 × 0.25 = $187,500. Step 2: Calculate annual cash flow: NOI - Debt Service = $52,500 - $38,000 = $14,500. Step 3: Divide cash flow by cash invested: $14,500 ÷ $187,500 = 0.0773 = 7.73%, which rounds to 7.7%. This metric shows the return on the investor's actual cash investment, not the total property value. It's a key metric for evaluating investment properties in Texas."
  },
  {
    id: 260,
    question: "A rectangular office space is 60 feet by 90 feet. Annual rent is $28 per sq ft. What is the monthly rent?",
    answer: "$12,600",
    category: "Math Calculations",
    difficulty: "medium",
    explanation: "Step 1: Calculate the area in square feet: 60 feet × 90 feet = 5,400 square feet. Step 2: Calculate annual rent: 5,400 sq ft × $28 per sq ft = $151,200 per year. Step 3: Convert annual rent to monthly rent: $151,200 ÷ 12 months = $12,600 per month. Commercial leases in Texas often quote rent per square foot per year, so you must calculate the total annual rent first, then divide by 12 to get the monthly amount."
  },
  {
    id: 261,
    question: "A property sold for $500,000. In Texas, there is no state transfer tax, but some counties charge fees. If a county charges $0.50 per $500 (or fraction), what is the transfer fee?",
    answer: "$500",
    category: "Math Calculations",
    difficulty: "hard",
    explanation: "Texas does not have a state transfer tax, but some counties may charge transfer fees. Step 1: Round UP to the nearest $500: $500,000 is already a multiple of $500, so no rounding needed. Step 2: Divide by $500: $500,000 ÷ $500 = 1,000. Step 3: Multiply by $0.50: 1,000 × $0.50 = $500. Note: If the sale price had been $500,100, you would round up to $500,500 before calculating. Transfer fees in Texas vary by county and are typically much lower than states with transfer taxes. They are usually paid by the seller at closing."
  },
  {
    id: 262,
    question: "How many linear feet of fencing are needed to enclose a rectangular lot that is 180 feet by 250 feet?",
    answer: "860 feet",
    category: "Math Calculations",
    difficulty: "easy",
    explanation: "To find the perimeter (total length of fencing needed), use the formula: Perimeter = 2 × (Length + Width). Step 1: Add the length and width: 180 feet + 250 feet = 430 feet. Step 2: Multiply by 2: 2 × 430 = 860 feet. This gives the total linear feet of fencing needed to enclose the rectangular lot. Linear feet means the total length in a straight line, which is what you need for fencing calculations."
  },
  {
    id: 263,
    question: "A commission of $22,500 was paid on a sale. The commission rate was 6%. What was the sales price?",
    answer: "$375,000",
    category: "Math Calculations",
    difficulty: "easy",
    explanation: "This is a reverse commission calculation - finding the sales price when you know the commission amount and rate. Formula: Sales Price = Commission ÷ Commission Rate (as decimal). Step 1: Convert 6% to a decimal: 6% = 0.06. Step 2: Divide the commission by the commission rate: $22,500 ÷ 0.06 = $375,000. To verify: $375,000 × 0.06 = $22,500. This type of calculation is useful when you know the commission paid and need to determine what the property sold for."
  },
  {
    id: 264,
    question: "A property has an assessed value of $320,000. The local tax rate is 2.3%. What are the annual property taxes?",
    answer: "$7,360",
    category: "Math Calculations",
    difficulty: "easy",
    explanation: "Property taxes in Texas are calculated by multiplying the assessed value by the tax rate. Step 1: Convert 2.3% to a decimal: 2.3% = 0.023. Step 2: Multiply assessed value by tax rate: $320,000 × 0.023 = $7,360. Texas property tax rates vary by county, city, and school district, and can be higher than many other states. The assessed value is typically based on the property's market value as determined by the county appraisal district."
  },
  {
    id: 265,
    question: "A property sold for $420,000, which was 92% of the listing price. What was the listing price?",
    answer: "$456,521.74",
    category: "Math Calculations",
    difficulty: "medium",
    explanation: "This is a reverse percentage calculation. If the sale price is 92% of the listing price, then the listing price is the sale price divided by 0.92. Step 1: Convert 92% to a decimal: 0.92. Step 2: Divide the sale price by the percentage (as a decimal): $420,000 ÷ 0.92 = $456,521.74. To verify: $456,521.74 × 0.92 = $420,000. This type of calculation is useful when you know the final amount and need to find the original amount before a percentage reduction or increase."
  },
  {
    id: 266,
    question: "An investor purchased a property for $650,000 and sold it 4 years later for $780,000. What is the total appreciation percentage?",
    answer: "20%",
    category: "Math Calculations",
    difficulty: "medium",
    explanation: "Appreciation is the increase in property value over time. Formula: Appreciation % = [(Sale Price - Purchase Price) ÷ Purchase Price] × 100. Step 1: Calculate the dollar amount of appreciation: $780,000 - $650,000 = $130,000. Step 2: Divide by the purchase price: $130,000 ÷ $650,000 = 0.20. Step 3: Convert to percentage: 0.20 × 100 = 20%. This represents a 20% total appreciation over the 4-year period. To find annual appreciation, divide by the number of years (20% ÷ 4 = 5% per year), but this question asks for total appreciation."
  },
  {
    id: 267,
    question: "A buyer obtains a $450,000 loan with a 4.75% interest rate. What is the annual interest?",
    answer: "$21,375",
    category: "Math Calculations",
    difficulty: "easy",
    explanation: "Annual interest is calculated by multiplying the loan amount by the annual interest rate (as a decimal). Step 1: Convert 4.75% to a decimal: 4.75% = 0.0475. Step 2: Multiply loan amount by interest rate: $450,000 × 0.0475 = $21,375. This is the total interest that would be paid in one year if the loan balance remained constant. On an amortized loan, the interest portion of each payment decreases over time as the principal is paid down."
  },
  {
    id: 268,
    question: "A property has an NOI of $42,000. If the cap rate is 7%, what is the estimated value?",
    answer: "$600,000",
    category: "Math Calculations",
    difficulty: "medium",
    explanation: "The capitalization (cap) rate method estimates property value using the income approach. Formula: Value = NOI ÷ Cap Rate. Step 1: Convert 7% cap rate to a decimal: 0.07. Step 2: Divide NOI by cap rate: $42,000 ÷ 0.07 = $600,000. A higher cap rate indicates higher risk or lower value; a lower cap rate indicates lower risk or higher value. This method is commonly used for income-producing properties in Texas, especially for commercial real estate investments."
  },
  {
    id: 269,
    question: "How many acres is 1/2 of a section?",
    answer: "320 acres",
    category: "Math Calculations",
    difficulty: "medium",
    explanation: "A section is a standard unit of land measurement in the U.S. Public Land Survey System. One full section equals 640 acres. To find 1/2 of a section: 640 acres ÷ 2 = 320 acres. Alternatively: 640 × 0.5 = 320 acres. A half section is a common land division in Texas. Other common divisions: 1/4 section = 160 acres, 1/8 section (half of a quarter) = 80 acres. This measurement system is used in Texas and other western states."
  },
  {
    id: 270,
    question: "A property's after-tax cash flow is $18,000 and the initial equity was $150,000. What is the after-tax return on equity?",
    answer: "12%",
    category: "Math Calculations",
    difficulty: "hard",
    explanation: "After-tax return on equity measures the percentage return on the investor's equity investment after all expenses and taxes. Formula: Return on Equity = After-Tax Cash Flow ÷ Initial Equity. Step 1: Divide after-tax cash flow by initial equity: $18,000 ÷ $150,000 = 0.12. Step 2: Convert to percentage: 0.12 × 100 = 12%. This metric shows how well the investor's equity is performing. After-tax cash flow is the money remaining after all operating expenses, debt service (mortgage payments), and income taxes. Initial equity is the down payment plus any additional capital invested."
  },
  {
    id: 271,
    question: "A rectangular lot measures 150 feet by 200 feet. How many square feet is it?",
    answer: "30,000 square feet",
    category: "Math Calculations",
    difficulty: "easy",
    explanation: "To find the area of a rectangle, multiply length by width. Step 1: Multiply the dimensions: 150 feet × 200 feet = 30,000 square feet. This is a basic area calculation used frequently in real estate for determining lot sizes, building footprints, and property descriptions. To convert to acres: 30,000 ÷ 43,560 = approximately 0.69 acres."
  },
  {
    id: 272,
    question: "A property has a monthly rent of $1,800. If the rent is prorated for 12 days, what is the prorated amount?",
    answer: "$720",
    category: "Math Calculations",
    difficulty: "easy",
    explanation: "Prorated rent divides the monthly rent based on the number of days occupied. Formula: (Monthly Rent ÷ 30) × Days Occupied. Step 1: Calculate daily rent: $1,800 ÷ 30 = $60 per day. Step 2: Multiply by days occupied: $60 × 12 = $720. This calculation is commonly used when a tenant moves in or out mid-month, or when closing occurs mid-month and rent needs to be prorated between buyer and seller."
  },
  {
    id: 273,
    question: "A property has a gross income of $95,000 and operating expenses of $38,000. What is the NOI?",
    answer: "$57,000",
    category: "Math Calculations",
    difficulty: "easy",
    explanation: "NOI (Net Operating Income) is calculated by subtracting operating expenses from gross income. Formula: NOI = Gross Income - Operating Expenses. Step 1: Subtract operating expenses from gross income: $95,000 - $38,000 = $57,000. NOI is the income remaining after all operating expenses but before debt service (mortgage payments) and income taxes. It's a key metric for income-producing properties and is used in the income approach to valuation."
  },
  {
    id: 274,
    question: "A property sold for $2,500 per square foot. If the property is 2,400 square feet, what was the sale price?",
    answer: "$6,000,000",
    category: "Math Calculations",
    difficulty: "easy",
    explanation: "To find the sale price when given price per square foot, multiply the price per square foot by the total square footage. Step 1: Multiply price per square foot by square footage: $2,500 × 2,400 = $6,000,000. This calculation is commonly used in commercial real estate where properties are often quoted and compared on a price-per-square-foot basis."
  },
  {
    id: 275,
    question: "A seller's net proceeds are $185,000. The sale price was $220,000. What percentage of the sale price went to costs?",
    answer: "15.9%",
    category: "Math Calculations",
    difficulty: "medium",
    explanation: "To find what percentage went to costs, first calculate the total costs, then divide by the sale price. Step 1: Calculate total costs: $220,000 - $185,000 = $35,000. Step 2: Divide costs by sale price: $35,000 ÷ $220,000 = 0.1591. Step 3: Convert to percentage: 0.1591 × 100 = 15.91%, which rounds to 15.9%. This shows what portion of the sale price was consumed by commissions, closing costs, loan payoffs, and other expenses."
  },
  {
    id: 276,
    question: "What is a Texas real estate broker?",
    answer: "A person licensed to operate a real estate brokerage business in Texas.",
    category: "Agency/Brokerage",
    difficulty: "easy",
    explanation: "Brokers can employ sales agents and operate independently."
  },
  {
    id: 277,
    question: "What is a Texas real estate sales agent?",
    answer: "A person licensed to perform real estate activities under a broker's supervision.",
    category: "Agency/Brokerage",
    difficulty: "easy",
    explanation: "Sales agents must work under a licensed broker's supervision."
  },
  {
    id: 278,
    question: "What is a Texas broker-salesperson?",
    answer: "A person licensed as both broker and salesperson who works under another broker.",
    category: "Agency/Brokerage",
    difficulty: "medium",
    explanation: "Has broker license but chooses to work under another broker's supervision."
  },
  {
    id: 279,
    question: "What is a Texas sales agent's license?",
    answer: "A license that allows performance of real estate activities under supervision.",
    category: "Agency/Brokerage",
    difficulty: "easy",
    explanation: "Must work under a licensed broker's supervision."
  },
  {
    id: 280,
    question: "What is the duty of disclosure in real estate agency?",
    answer: "The duty to reveal all material facts about a property or transaction that could affect a party's decision-making.",
    category: "Standards of Conduct",
    difficulty: "medium",
    explanation: "Disclosure of material facts is required nationally under agency law. TREC's requirements mirror these national standards. Agents must inform clients of all known material facts, including property defects, neighborhood issues, and any conflicts of interest."
  },
  {
    id: 281,
    question: "A Texas real estate agent represents a seller. The agent's buyer client wants to purchase the seller's property. Can the agent represent both parties, and if so, what must occur?",
    answer: "Yes, but only with informed written consent from both parties. The agent must fully disclose the dual agency relationship and its limitations before proceeding.",
    category: "Standards of Conduct",
    difficulty: "hard",
    explanation: "Dual agency is permitted in Texas but requires full disclosure and written consent from both parties. The agent's ability to represent either party is limited because they cannot fully advocate for one party's interests against the other. Without proper consent, the agent cannot proceed."
  },
  {
    id: 282,
    question: "What is designated agency in Texas?",
    answer: "When different licensees in the same firm represent different parties.",
    category: "Standards of Conduct",
    difficulty: "hard",
    explanation: "Allows one firm to represent both sides through different licensees."
  },
  {
    id: 283,
    question: "What is subagency in Texas?",
    answer: "When a licensee represents another licensee's client.",
    category: "Standards of Conduct",
    difficulty: "hard",
    explanation: "Creates fiduciary relationship between subagent and original client."
  },
  {
    id: 284,
    question: "What is the difference between a Texas addendum and an amendment?",
    answer: "An addendum adds new terms to a contract, while an amendment modifies existing terms. Both must be in writing and signed by all parties.",
    category: "Contracts",
    difficulty: "medium",
    explanation: "Addenda add new provisions (like inspection addendum), while amendments change existing contract terms (like changing the closing date)."
  },
  {
    id: 285,
    question: "What are TREC's duties?",
    answer: "Regulate real estate licensees, enforce laws, and protect consumers.",
    category: "Commission Duties & Powers",
    difficulty: "easy",
    explanation: "TREC oversees licensing, education, and disciplinary actions."
  },
  {
    id: 286,
    question: "What are TREC's powers?",
    answer: "Issue licenses, investigate complaints, and impose disciplinary actions.",
    category: "Commission Duties & Powers",
    difficulty: "easy",
    explanation: "TREC can suspend, revoke, or fine licensees for violations."
  },
  {
    id: 287,
    question: "What is TREC's consumer protection role?",
    answer: "Protecting the public from unlicensed and unethical real estate practices.",
    category: "Commission Duties & Powers",
    difficulty: "easy",
    explanation: "Investigates complaints and takes action against violators."
  },
  {
    id: 288,
    question: "What are Texas license requirements?",
    answer: "Education, examination, background check, and application requirements.",
    category: "Licensing",
    difficulty: "easy",
    explanation: "Must meet all requirements before license can be issued."
  },
  {
    id: 289,
    question: "What is community property in Texas?",
    answer: "Property acquired during marriage that belongs to both spouses equally.",
    category: "Special Topics",
    difficulty: "medium",
    explanation: "Texas is a community property state with specific rules for marital property."
  },
  {
    id: 290,
    question: "What is separate property in Texas?",
    answer: "Property owned by one spouse before marriage or acquired by gift or inheritance.",
    category: "Special Topics",
    difficulty: "medium",
    explanation: "Remains the individual property of the spouse who owns it."
  },
  {
    id: 291,
    question: "What is a Texas HOA?",
    answer: "Homeowners Association that manages common areas and enforces restrictions.",
    category: "Special Topics",
    difficulty: "easy",
    explanation: "Can impose fees, restrictions, and rules on property owners."
  },
  {
    id: 292,
    question: "What are Texas landlord-tenant laws?",
    answer: "State laws governing rental relationships and property rights.",
    category: "Special Topics",
    difficulty: "medium",
    explanation: "Include security deposits, eviction procedures, and tenant rights."
  },
  {
    id: 293,
    question: "What is a Texas mechanic's lien?",
    answer: "A lien filed by contractors or suppliers for unpaid work or materials.",
    category: "Special Topics",
    difficulty: "medium",
    explanation: "Must be filed within specific time limits and can affect property title."
  },
  {
    id: 294,
    question: "What is Texas homestead protection?",
    answer: "Legal protection for a person's primary residence from certain creditors.",
    category: "Special Topics",
    difficulty: "medium",
    explanation: "Limits the amount of equity that can be reached by creditors."
  },
  {
    id: 295,
    question: "What is Texas adverse possession?",
    answer: "Acquiring title to property through continuous, hostile possession.",
    category: "Special Topics",
    difficulty: "hard",
    explanation: "Requires open, notorious, continuous, and hostile possession for statutory period."
  },
  {
    id: 296,
    question: "What is Texas eminent domain?",
    answer: "Government's power to take private property for public use with compensation.",
    category: "Special Topics",
    difficulty: "medium",
    explanation: "Must provide just compensation and follow due process requirements."
  },
  {
    id: 297,
    question: "What is Texas homestead exemption?",
    answer: "Property tax reduction for a person's primary residence, reducing taxable value by at least $100,000 for school taxes.",
    category: "Special Topics",
    difficulty: "medium",
    explanation: "The basic homestead exemption reduces the taxable value by $100,000 for school taxes, with additional exemptions available for seniors, disabled persons, and veterans."
  },
  {
    id: 298,
    question: "What is Texas property tax proration?",
    answer: "The division of property taxes between buyer and seller at closing based on the number of days each party owned the property.",
    category: "Special Topics",
    difficulty: "medium",
    explanation: "Seller is debited for days they owned the property; buyer is credited for those same days since they'll be responsible for future taxes. Calculated as (Annual Tax ÷ 365) × Days Owned."
  },
  {
    id: 299,
    question: "What is a Texas property tax lien?",
    answer: "A lien placed on property for unpaid property taxes, which has priority over most other liens and can result in foreclosure.",
    category: "Special Topics",
    difficulty: "medium",
    explanation: "Property tax liens are superior to most other liens and can result in tax foreclosure if taxes remain unpaid for an extended period."
  },
  {
    id: 300,
    question: "What is the Texas property tax cap?",
    answer: "A limitation preventing property taxes from increasing more than 10% per year for homestead properties.",
    category: "Special Topics",
    difficulty: "medium",
    explanation: "The 10% cap protects homeowners from dramatic tax increases, though it doesn't apply to new construction or improvements."
  },
  {
    id: 301,
    question: "What is the priority of property tax liens in Texas?",
    answer: "Property tax liens have priority over most other liens, including mortgages, and can result in foreclosure even if other liens exist.",
    category: "Special Topics",
    difficulty: "hard",
    explanation: "In Texas, property tax liens are superior liens that take priority over mortgages and most other encumbrances. This means unpaid property taxes can result in foreclosure that wipes out other liens, making it critical to ensure property taxes are paid."
  },
  {
    id: 302,
    question: "What is the Texas Property Code?",
    answer: "State law governing landlord-tenant relationships, property rights, and real estate transactions.",
    category: "Special Topics",
    difficulty: "medium",
    explanation: "Contains specific provisions for security deposits, eviction procedures, and tenant rights that differ from other states."
  },
  {
    id: 303,
    question: "What is the maximum security deposit allowed in Texas?",
    answer: "No statutory maximum, but must be reasonable and returned within 30 days of move-out.",
    category: "Special Topics",
    difficulty: "medium",
    explanation: "Unlike some states, Texas doesn't cap security deposits, but landlords must return deposits within 30 days or provide itemized deductions."
  },
  {
    id: 304,
    question: "What is the notice requirement for eviction in Texas?",
    answer: "Three days' notice to vacate for non-payment of rent, or notice as specified in lease for other violations.",
    category: "Special Topics",
    difficulty: "medium",
    explanation: "Texas has specific eviction procedures that must be followed exactly, including proper notice and court proceedings."
  },
  {
    id: 305,
    question: "What is a TREC-promulgated contract form?",
    answer: "A standardized real estate contract form approved by the Texas Real Estate Commission for use in transactions.",
    category: "Contracts",
    difficulty: "medium",
    explanation: "TREC creates and maintains standard contract forms that licensees must use, including One to Four Family Residential Contract, Unimproved Property Contract, and New Home Contract."
  },
  {
    id: 306,
    question: "A Texas seller knows their home has foundation problems but doesn't mention it to the buyer. The buyer signs the contract and later discovers the issue. What did the seller fail to provide?",
    answer: "The Seller's Disclosure Notice, which is required in Texas and must be provided before contract execution, disclosing all known material defects including structural problems.",
    category: "Property Disclosures & Environmental Issues",
    difficulty: "medium",
    explanation: "Texas requires sellers to provide the Seller's Disclosure Notice before contract execution. Failure to disclose known material defects can result in the buyer rescinding the contract, suing for damages, or both. Foundation problems are definitely material defects that must be disclosed."
  },
  {
    id: 307,
    question: "A Texas real estate agent knowingly tells a buyer that a property has no foundation issues, when the agent actually knows there are serious problems. The buyer purchases the property and discovers the truth. What law has the agent violated, and what are the potential consequences?",
    answer: "The Texas Deceptive Trade Practices Act (DTPA). The agent could face treble damages (3x actual damages), plus attorney fees, for the knowing misrepresentation.",
    category: "Standards of Conduct",
    difficulty: "hard",
    explanation: "DTPA protects consumers from deceptive practices in real estate. Knowingly misrepresenting material facts like foundation problems is a clear DTPA violation. The treble damages provision (3x actual damages) makes this a serious violation with severe financial consequences for the agent."
  },
  {
    id: 308,
    question: "What is a surface estate versus mineral estate in Texas?",
    answer: "Surface estate is the right to use the land surface, while mineral estate is the right to extract oil, gas, and minerals below the surface.",
    category: "Forms of Ownership, Transfer & Recording",
    difficulty: "hard",
    explanation: "In Texas, these can be separately owned. The mineral estate is dominant and has the right to reasonable use of the surface to extract minerals."
  },
  {
    id: 309,
    question: "What is an oil and gas lease in Texas?",
    answer: "A contract granting the right to explore for and produce oil and gas from a property in exchange for royalties.",
    category: "Special Topics",
    difficulty: "hard",
    explanation: "Common in Texas, these leases grant surface access rights and typically include royalty payments (usually 1/8 or 12.5% of production)."
  },
  {
    id: 310,
    question: "What is Texas homestead protection from creditors?",
    answer: "Legal protection that prevents most creditors from forcing the sale of a homestead to satisfy debts, with specific equity limits.",
    category: "Special Topics",
    difficulty: "hard",
    explanation: "Protects up to 10 acres in urban areas or 100 acres in rural areas (200 acres for families) from forced sale by most creditors, except for mortgages, tax liens, and mechanic's liens."
  },
  {
    id: 311,
    question: "What is required to designate a Texas homestead?",
    answer: "The property must be the owner's primary residence, and they must file a homestead designation with the county clerk.",
    category: "Special Topics",
    difficulty: "medium",
    explanation: "Must be the owner's principal residence, and designation provides both property tax benefits and creditor protection."
  },
  {
    id: 312,
    question: "A married couple in Texas wants to sell their home. Only the husband's name is on the deed, but they bought it during their marriage. Can the husband sell the property without his wife's signature?",
    answer: "No. In Texas, both spouses must sign the deed and contract because property acquired during marriage is community property, regardless of whose name is on the title.",
    category: "Forms of Ownership, Transfer & Recording",
    difficulty: "medium",
    explanation: "Texas is a community property state. Property acquired during marriage is owned equally by both spouses, even if only one name appears on the title. Both spouses must join in the transaction for it to be valid. A sale without both signatures may be voidable."
  },
  {
    id: 313,
    question: "What is separate property in Texas community property law?",
    answer: "Property owned before marriage, acquired by gift or inheritance, or property acquired with separate property funds.",
    category: "Forms of Ownership, Transfer & Recording",
    difficulty: "medium",
    explanation: "Separate property remains individual property and doesn't require spousal consent for transactions, but proving separate property status can be complex."
  },
  {
    id: 314,
    question: "What is the One to Four Family Residential Contract (Resale)?",
    answer: "The standard TREC-promulgated contract form used for most residential property sales in Texas.",
    category: "Contracts",
    difficulty: "medium",
    explanation: "This is the most commonly used contract form in Texas residential transactions and includes all required disclosures and contingencies."
  },
  {
    id: 315,
    question: "What is the Unimproved Property Contract in Texas?",
    answer: "A TREC-promulgated contract form used for the sale of vacant land or unimproved property.",
    category: "Contracts",
    difficulty: "medium",
    explanation: "Specifically designed for land sales and includes provisions for access, utilities, and development restrictions."
  },
  {
    id: 316,
    question: "What is the New Home Contract in Texas?",
    answer: "A TREC-promulgated contract form used for the sale of newly constructed homes.",
    category: "Contracts",
    difficulty: "medium",
    explanation: "Includes specific provisions for warranties, builder obligations, and new construction disclosures."
  },
  {
    id: 317,
    question: "What are the remedies available under the Texas DTPA?",
    answer: "Actual damages, treble damages (3x) for knowing violations, mental anguish damages, and attorney fees.",
    category: "Standards of Conduct",
    difficulty: "hard",
    explanation: "DTPA provides strong consumer protection with treble damages for knowing violations, making it important for licensees to avoid deceptive practices."
  },
  {
    id: 318,
    question: "What is required disclosure under Texas DTPA for real estate?",
    answer: "Licensees must disclose all material facts about property condition, defects, and any known problems that could affect value or desirability.",
    category: "Standards of Conduct",
    difficulty: "medium",
    explanation: "Failure to disclose material facts can result in DTPA violations, which carry severe penalties including treble damages."
  },
  {
    id: 319,
    question: "What is the difference between surface rights and mineral rights in Texas?",
    answer: "Surface rights allow use of the land surface, while mineral rights allow extraction of subsurface resources, and they can be separately owned.",
    category: "Forms of Ownership, Transfer & Recording",
    difficulty: "hard",
    explanation: "In Texas, mineral rights are dominant, meaning the mineral owner has the right to reasonable surface access for extraction, even if it disrupts surface use."
  },
  {
    id: 320,
    question: "What is a royalty interest in Texas oil and gas?",
    answer: "A percentage interest in the production revenue from oil and gas extracted from a property, typically 1/8 (12.5%) or as negotiated.",
    category: "Special Topics",
    difficulty: "hard",
    explanation: "Royalty owners receive a percentage of production revenue without paying drilling costs, making it a valuable property interest in Texas."
  },
  {
    id: 321,
    question: "What is a working interest in Texas oil and gas?",
    answer: "An ownership interest that bears the costs of drilling and production in exchange for a share of the profits.",
    category: "Special Topics",
    difficulty: "hard",
    explanation: "Working interest owners pay their share of drilling and operating costs but receive a larger percentage of production revenue than royalty owners."
  },
  {
    id: 322,
    question: "What is required for spousal consent in Texas real estate transactions?",
    answer: "Both spouses must sign deeds and mortgages for community property, even if only one spouse's name appears on the title.",
    category: "Forms of Ownership, Transfer & Recording",
    difficulty: "medium",
    explanation: "Texas community property law requires both spouses to join in transactions involving community property to protect the non-title spouse's interest."
  },
  {
    id: 323,
    question: "A Texas couple's home is in the husband's name only, but they bought it during their marriage. The husband sells the property without his wife's knowledge or signature. What are the potential consequences?",
    answer: "The transaction may be voidable, and the wife can potentially set aside the sale or claim her community property interest, as both spouses must sign for community property transactions.",
    category: "Forms of Ownership, Transfer & Recording",
    difficulty: "hard",
    explanation: "In Texas, property acquired during marriage is community property, regardless of whose name is on the title. Both spouses must join in the transaction. A sale without both signatures can be challenged, potentially invalidated, and the non-joining spouse may have legal remedies to recover their interest."
  },
  {
    id: 324,
    question: "What is the homestead exemption amount in Texas?",
    answer: "School districts must offer at least $100,000 exemption, with additional exemptions available for seniors, disabled persons, and veterans.",
    category: "Special Topics",
    difficulty: "medium",
    explanation: "The basic homestead exemption reduces the taxable value by $100,000 for school taxes, with additional exemptions available for qualifying property owners."
  },
  {
    id: 325,
    question: "What is required to claim a Texas homestead exemption?",
    answer: "The property must be the owner's principal residence, and an application must be filed with the county appraisal district by April 30th.",
    category: "Special Topics",
    difficulty: "medium",
    explanation: "Must be filed annually (or when property changes) and requires proof of residency and ownership."
  },
  {
    id: 326,
    question: "What is Texas property tax foreclosure?",
    answer: "The process of selling property to collect unpaid property taxes, conducted by county tax offices when taxes remain delinquent.",
    category: "Special Topics",
    difficulty: "medium",
    explanation: "Property can be sold at a tax sale if taxes remain unpaid. The property owner typically has a redemption period to reclaim the property by paying all taxes and penalties."
  },
  {
    id: 327,
    question: "What is the Texas property tax rate?",
    answer: "The rate at which property taxes are calculated, expressed as dollars per $100 of assessed value.",
    category: "Special Topics",
    difficulty: "easy",
    explanation: "Property tax rates vary by county, city, and school district. The rate is multiplied by the assessed value (divided by 100) to calculate the tax amount."
  },
  {
    id: 328,
    question: "What is a Texas property tax appeal?",
    answer: "The process of challenging a property tax assessment through the county appraisal review board.",
    category: "Special Topics",
    difficulty: "medium",
    explanation: "Property owners can appeal if they believe their assessment is too high. The appeal must be filed by the deadline (typically May 15th) and may require evidence of comparable properties."
  },
  {
    id: 329,
    question: "What are the continuing education requirements for Texas real estate licensees?",
    answer: "Sales agents must complete 18 hours of continuing education every two years, including 10 hours of legal updates and 8 hours of elective courses.",
    category: "Licensing",
    difficulty: "medium",
    explanation: "TREC requires continuing education for license renewal. The 10 hours of legal updates must cover current Texas real estate law, and the 8 elective hours can cover various topics like contracts, finance, or property management."
  },
  {
    id: 330,
    question: "How often must Texas real estate licenses be renewed?",
    answer: "Texas real estate licenses must be renewed every two years on the licensee's birth month.",
    category: "Licensing",
    difficulty: "easy",
    explanation: "License renewal is required biennially, and licensees must complete continuing education requirements before renewal. Late renewals may incur penalties."
  },
  {
    id: 331,
    question: "What happens if a Texas real estate licensee fails to complete continuing education?",
    answer: "The license cannot be renewed, and the licensee cannot practice real estate until continuing education is completed and the license is renewed.",
    category: "Licensing",
    difficulty: "medium",
    explanation: "TREC will not renew a license without proof of completed continuing education. Practicing with an expired license can result in disciplinary action."
  },
  {
    id: 332,
    question: "What disciplinary actions can TREC take against licensees?",
    answer: "TREC can issue warnings, impose fines, place licensees on probation, suspend licenses, or revoke licenses for violations of the Real Estate License Act or TREC rules.",
    category: "Standards of Conduct",
    difficulty: "hard",
    explanation: "Disciplinary actions depend on the severity of the violation. Minor violations may result in warnings or fines, while serious violations like fraud or misrepresentation can lead to license suspension or revocation."
  },
  {
    id: 333,
    question: "What violations can result in TREC license revocation?",
    answer: "Serious violations including fraud, misrepresentation, theft, conviction of a felony, or repeated violations of TREC rules can result in license revocation.",
    category: "Standards of Conduct",
    difficulty: "hard",
    explanation: "License revocation is the most severe penalty and typically results from criminal activity, serious ethical violations, or a pattern of misconduct that demonstrates the licensee is unfit to practice."
  },
  {
    id: 334,
    question: "What are the responsibilities of a Texas real estate broker versus a sales agent?",
    answer: "Brokers can operate independently, supervise sales agents, and are responsible for all transactions. Sales agents must work under a broker's supervision and cannot operate independently.",
    category: "Agency/Brokerage",
    difficulty: "medium",
    explanation: "Brokers have additional education and experience requirements and can own or manage a brokerage. Sales agents must be sponsored by a broker and all transactions must be conducted through the broker's office."
  },
  {
    id: 335,
    question: "A Texas sales agent wants to work independently and handle their own transactions. Can they do this?",
    answer: "No. Texas sales agents must work under a broker's supervision and all transactions must be conducted through the sponsoring broker's office.",
    category: "Agency/Brokerage",
    difficulty: "medium",
    explanation: "Sales agents cannot operate independently in Texas. They must be sponsored by a broker, and the broker is responsible for supervising the agent's activities and ensuring compliance with TREC rules."
  },
  {
    id: 336,
    question: "What is the difference between an independent contractor and an employee for Texas real estate licensees?",
    answer: "Independent contractors control their own schedule and methods, pay their own expenses, and receive commission. Employees work set hours, receive benefits, and are subject to more direct supervision.",
    category: "Agency/Brokerage",
    difficulty: "hard",
    explanation: "Most Texas real estate licensees work as independent contractors, which affects tax treatment, benefits, and the level of control the broker has over their activities. The classification has legal and tax implications."
  },
  {
    id: 337,
    question: "What is the TREC investigation process when a complaint is filed against a licensee?",
    answer: "TREC investigates complaints, may request documentation and responses from the licensee, holds hearings if necessary, and can take disciplinary action based on findings.",
    category: "Commission Duties & Powers",
    difficulty: "hard",
    explanation: "The investigation process allows TREC to gather evidence, hear from all parties, and make informed decisions about whether violations occurred and what disciplinary action, if any, is appropriate."
  },
  {
    id: 338,
    question: "A licensee receives a complaint from TREC. What should they do?",
    answer: "Respond promptly and completely to TREC's request, provide all requested documentation, and consider consulting with an attorney experienced in TREC matters.",
    category: "Commission Duties & Powers",
    difficulty: "medium",
    explanation: "Licensees must cooperate with TREC investigations. Failure to respond or provide requested information can result in additional violations. Professional legal assistance is recommended for serious complaints."
  },
  {
    id: 339,
    question: "What is the purpose of TREC's continuing education requirements?",
    answer: "To ensure licensees stay current with changes in real estate law, regulations, and best practices, maintaining competency and protecting consumers.",
    category: "Licensing",
    difficulty: "easy",
    explanation: "Continuing education helps licensees stay informed about legal updates, market changes, and ethical standards, ensuring they provide competent service to consumers and comply with current regulations."
  }
];
